import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * ProviderStatsCard
 * -----------------
 * Compact card showing the Direct MiniMax M3 ↔ OpenRouter provider stats
 * for one service (Story Idea Generator or Paraphraser).
 *
 * Props:
 *   - title: 'Story Idea Generator' | 'Paraphraser'
 *   - stats: { currentProvider, minimaxCalls, minimaxFailures, openrouterCalls, openrouterFailures, lastError }
 */
function ProviderStatsCard({ title, stats }) {
  if (!stats) {
    return (
      <div style={{
        padding: '14px',
        border: '1px solid #e1e4e8',
        borderRadius: '8px',
        background: '#fafbfc',
        fontSize: '13px',
        color: '#888'
      }}>
        <div style={{ fontWeight: 600, marginBottom: '6px' }}>{title}</div>
        <div>No calls yet.</div>
      </div>
    );
  }

  const provider = stats.currentProvider;
  const providerColor = provider === 'minimax' ? '#0d6efd' : provider === 'openrouter' ? '#6c757d' : '#888';
  const providerBg = provider === 'minimax' ? '#e7f1ff' : provider === 'openrouter' ? '#f0f0f0' : '#fafbfc';
  const providerLabel = provider === 'minimax' ? 'MiniMax M3 (direct)' : provider === 'openrouter' ? 'OpenRouter (fallback)' : '— no calls yet —';

  return (
    <div style={{
      padding: '14px',
      border: '1px solid #e1e4e8',
      borderRadius: '8px',
      background: '#ffffff',
      fontSize: '13px'
    }}>
      <div style={{ fontWeight: 600, marginBottom: '8px', color: '#333' }}>{title}</div>
      <div style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '12px',
        background: providerBg,
        color: providerColor,
        fontWeight: 600,
        fontSize: '12px',
        marginBottom: '10px'
      }}>
        {providerLabel}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
        <div>
          <div style={{ color: '#888', fontSize: '11px' }}>MiniMax direct calls</div>
          <div style={{ fontWeight: 600, fontSize: '16px', color: '#0d6efd' }}>{stats.minimaxCalls ?? 0}</div>
        </div>
        <div>
          <div style={{ color: '#888', fontSize: '11px' }}>MiniMax failures</div>
          <div style={{ fontWeight: 600, fontSize: '16px', color: stats.minimaxFailures ? '#dc3545' : '#6c757d' }}>
            {stats.minimaxFailures ?? 0}
          </div>
        </div>
        <div>
          <div style={{ color: '#888', fontSize: '11px' }}>OpenRouter calls</div>
          <div style={{ fontWeight: 600, fontSize: '16px', color: '#6c757d' }}>{stats.openrouterCalls ?? 0}</div>
        </div>
        <div>
          <div style={{ color: '#888', fontSize: '11px' }}>OpenRouter failures</div>
          <div style={{ fontWeight: 600, fontSize: '16px', color: stats.openrouterFailures ? '#dc3545' : '#6c757d' }}>
            {stats.openrouterFailures ?? 0}
          </div>
        </div>
      </div>
      {stats.lastError && (
        <div style={{
          marginTop: '8px',
          padding: '6px 8px',
          background: '#fff3cd',
          border: '1px solid #ffeeba',
          borderRadius: '4px',
          fontSize: '11px',
          color: '#856404',
          wordBreak: 'break-word'
        }}>
          ⚠️ {stats.lastError}
        </div>
      )}
    </div>
  );
}

/**
 * RuntimeStatusPanel
 * ------------------
 * Two columns showing last 10 LLM failures + last 10 error outputs
 * across all LLM-touching services.
 */
function RuntimeStatusPanel({ llmFailures, errorOutputs, loading }) {
  if (loading && (!llmFailures || llmFailures.length === 0) && (!errorOutputs || errorOutputs.length === 0)) {
    return (
      <div className="admin-loading" style={{ marginBottom: '15px' }}>
        Loading runtime status…
      </div>
    );
  }

  const totalFailures = (llmFailures?.length || 0) + (errorOutputs?.length || 0);

  if (totalFailures === 0) {
    return (
      <div style={{
        padding: '16px',
        background: '#e8f5e9',
        border: '1px solid #c8e6c9',
        borderRadius: '8px',
        marginBottom: '15px',
        textAlign: 'center',
        color: '#2e7d32',
        fontWeight: 500
      }}>
        ✅ No failures in the last few minutes — looks healthy
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
      gap: '12px',
      marginBottom: '15px'
    }}>
      <FailureColumn
        title={`❌ Last ${llmFailures.length} LLM Failures`}
        entries={llmFailures}
        accentColor="#dc3545"
        bgColor="#fff5f5"
      />
      <FailureColumn
        title={`⚠️ Last ${errorOutputs.length} Error Outputs`}
        entries={errorOutputs}
        accentColor="#ffc107"
        bgColor="#fffdf5"
      />
    </div>
  );
}

function FailureColumn({ title, entries, accentColor, bgColor }) {
  return (
    <div style={{
      padding: '12px',
      border: '1px solid #e1e4e8',
      borderRadius: '8px',
      background: bgColor
    }}>
      <div style={{
        fontWeight: 600,
        color: accentColor,
        marginBottom: '10px',
        fontSize: '13px'
      }}>
        {title}
      </div>
      {entries.length === 0 ? (
        <div style={{ color: '#888', fontSize: '12px', fontStyle: 'italic' }}>
          None in window.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '400px', overflowY: 'auto' }}>
          {entries.map((e, idx) => (
            <FailureCard key={idx} entry={e} />
          ))}
        </div>
      )}
    </div>
  );
}

function FailureCard({ entry }) {
  const ts = entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : '—';
  const providerColor =
    entry.provider === 'minimax' ? '#0d6efd' :
    entry.provider === 'openrouter' ? '#6c757d' :
    entry.provider === 'gemini' ? '#28a745' :
    entry.provider === 'chain_exhausted' ? '#dc3545' :
    '#888';

  const ctxTags = [];
  if (entry.context?.ideaId) ctxTags.push(`idea #${entry.context.ideaId}`);
  if (entry.context?.storyId) ctxTags.push(`story ${entry.context.storyId}`);
  if (entry.context?.queueId) ctxTags.push(`queue ${entry.context.queueId}`);
  if (entry.context?.rowId) ctxTags.push(`row #${entry.context.rowId}`);
  if (entry.context?.genre) ctxTags.push(entry.context.genre);
  if (entry.context?.slotGenre) ctxTags.push(`slot: ${entry.context.slotGenre}`);

  return (
    <div style={{
      padding: '8px 10px',
      background: 'white',
      border: '1px solid #f0f0f0',
      borderRadius: '6px',
      fontSize: '12px'
    }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap' }}>
        <span style={{ color: '#888', fontFamily: 'monospace', fontSize: '11px' }}>{ts}</span>
        {entry.service && (
          <span style={{
            padding: '1px 6px', background: '#f0f0f0', color: '#555',
            borderRadius: '8px', fontSize: '10px', fontWeight: 500
          }}>
            {entry.service}
          </span>
        )}
        {entry.provider && (
          <span style={{
            padding: '1px 6px', background: providerColor, color: 'white',
            borderRadius: '8px', fontSize: '10px', fontWeight: 500
          }}>
            {entry.provider}
          </span>
        )}
        {entry.stage && (
          <span style={{
            padding: '1px 6px', background: '#e9ecef', color: '#495057',
            borderRadius: '8px', fontSize: '10px', fontFamily: 'monospace'
          }}>
            {entry.stage}
          </span>
        )}
      </div>
      <div style={{
        color: '#333',
        wordBreak: 'break-word',
        marginBottom: ctxTags.length ? '4px' : 0
      }}>
        {entry.message}
      </div>
      {ctxTags.length > 0 && (
        <div style={{ color: '#888', fontSize: '10px' }}>
          {ctxTags.join(' · ')}
        </div>
      )}
    </div>
  );
}

/**
 * GenreReservoirTable
 * -------------------
 * 13-row table (one per supported genre) with:
 *   - Genre name
 *   - Available count (red ⚠ when below threshold)
 *   - Per-row "🔄 Generate" button (shows spinner while in-flight)
 *
 * `inFlight` is a Set<string> of genres currently generating.
 * `onTrigger(genre)` is the click handler.
 */
function GenreReservoirTable({ reservoir, threshold, inFlight, consumeInFlight, onTrigger, onConsume }) {
  const GENRES = [
    'Science Fiction', 'Romance', 'Mystery', 'Horror', 'Adventure',
    'Drama', 'Comedy', 'Folk', 'Fables', 'Spiritual',
    'Inspirational', 'Short Story', 'Children Stories'
  ];

  if (!reservoir) {
    return (
      <div className="admin-loading" style={{ marginBottom: '15px' }}>
        Loading per-genre reservoir counts…
      </div>
    );
  }

  return (
    <div style={{
      marginBottom: '15px',
      border: '1px solid #e1e4e8',
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ background: '#fafbfc', borderBottom: '1px solid #e1e4e8' }}>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#333' }}>Genre</th>
            <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: '#333', width: '120px' }}>
              Available
            </th>
            <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: '#333', width: '150px' }}>
              Consume
            </th>
            <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: '#333', width: '160px' }}>
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {GENRES.map((genre, idx) => {
            // The backend /status endpoint returns a rich object per genre:
            //   { available, target, threshold, needsReplenish, fallbacks }
            // Older/simple shapes (plain number) are still tolerated.
            const entry = reservoir[genre];
            const count = (entry && typeof entry === 'object' && 'available' in entry)
              ? entry.available
              : (typeof entry === 'number' ? entry : 0);
            const belowThreshold = count < threshold;
            const isInFlight = inFlight && inFlight.has && inFlight.has(genre);
            const isConsuming = consumeInFlight && consumeInFlight.has && consumeInFlight.has(genre);
            return (
              <tr key={genre} style={{
                borderBottom: idx === GENRES.length - 1 ? 'none' : '1px solid #f0f0f0',
                background: belowThreshold ? '#fff8f0' : 'transparent'
              }}>
                <td style={{ padding: '8px 12px', color: '#333' }}>
                  {genre}
                  {belowThreshold && (
                    <span style={{
                      marginLeft: '8px',
                      padding: '1px 6px',
                      background: '#dc3545',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '10px',
                      fontWeight: 600
                    }}>
                      ⚠ below {threshold}
                    </span>
                  )}
                </td>
                <td style={{
                  textAlign: 'center',
                  padding: '8px 12px',
                  fontWeight: 600,
                  color: belowThreshold ? '#dc3545' : '#28a745'
                }}>
                  {count}
                </td>
                <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                  <button
                    onClick={() => onConsume && onConsume(genre, 10)}
                    disabled={isConsuming || count === 0}
                    title={count === 0 ? 'No AVAILABLE ideas to consume' : 'Generate 10 stories from this genre\'s AVAILABLE ideas'}
                    style={{
                      padding: '4px 12px',
                      border: '1px solid #dc3545',
                      borderRadius: '6px',
                      background: isConsuming ? '#f0f0f0' : 'white',
                      color: isConsuming ? '#999' : '#dc3545',
                      cursor: isConsuming ? 'wait' : (count === 0 ? 'not-allowed' : 'pointer'),
                      fontSize: '12px',
                      fontWeight: 500,
                      opacity: count === 0 ? 0.5 : 1
                    }}
                  >
                    {isConsuming ? '⏳ Consuming…' : '🔥 Consume'}
                  </button>
                </td>
                <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                  <button
                    onClick={() => onTrigger(genre)}
                    disabled={isInFlight}
                    style={{
                      padding: '4px 12px',
                      border: '1px solid #667eea',
                      borderRadius: '6px',
                      background: isInFlight ? '#f0f0f0' : 'white',
                      color: isInFlight ? '#999' : '#667eea',
                      cursor: isInFlight ? 'wait' : 'pointer',
                      fontSize: '12px',
                      fontWeight: 500
                    }}
                  >
                    {isInFlight ? '⏳ Generating…' : '🔄 Generate'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AdminDashboard({ user, onBack }) {
  const [pendingStories, setPendingStories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [expandedStory, setExpandedStory] = useState(null);
  const [batchnames, setBatchnames] = useState([]);
  const [selectedBatchname, setSelectedBatchname] = useState('');
  const [confirmAction, setConfirmAction] = useState(null); // 'approve_all' or 'reject_all'
  const [prestageStories, setPrestageStories] = useState([]);
  const [stageStories, setStageStories] = useState([]);
  const [showPrestageSection, setShowPrestageSection] = useState(false);
  const [showPublishedSection, setShowPublishedSection] = useState(false);
  const [publishedStories, setPublishedStories] = useState([]);
  const [publishedLoading, setPublishedLoading] = useState(false);
  const [publishedSearch, setPublishedSearch] = useState('');
  const [publishedTotal, setPublishedTotal] = useState(0);
  const [publishedOffset, setPublishedOffset] = useState(0);
  const [deletingStoryId, setDeletingStoryId] = useState(null);

  // NEW: Rawstory state
  const [rawstories, setRawstories] = useState([]);
  const [rawstoryLoading, setRawstoryLoading] = useState(false);
  const [showRawstorySection, setShowRawstorySection] = useState(false);
  const [showRawstoryDropdown, setShowRawstoryDropdown] = useState(false);
  const [scrapeOnlyLoading, setScrapeOnlyLoading] = useState(false);

  // NEW: Finalstage state
  const [finalstageStories, setFinalstageStories] = useState([]);
  const [showFinalstageSection, setShowFinalstageSection] = useState(false);

  // ============================================================
  // WriteStory Workflow state — SEGREGATED into AI vs User.
  // Each side has its own state sets (queues by status + stats +
  // job running + skip in progress), and the existing
  // showWriteStorySection toggle is replaced with two toggles
  // (showAiWriteStorySection, showUserWriteStorySection) so the
  // admin dashboard renders them as separate collapsible panels.
  //
  // Segregation uses the llm_model column on story_queue:
  //   AI rows   -> llm_model IS NOT NULL  (StoryIdeaConsumer._consumeClaimedIdea)
  //   User rows -> llm_model IS NULL       (WriteStory.js user submit path)
  // ============================================================

  // --- AI WriteStory state ---
  const [aiWriteStoryImageQueue, setAiWriteStoryImageQueue] = useState([]);   // status = 3
  const [aiWriteStoryReadyQueue, setAiWriteStoryReadyQueue] = useState([]);     // status = 4
  const [aiWriteStoryRetryQueue, setAiWriteStoryRetryQueue] = useState([]);     // status = 6 (image_retry)
  const [aiWriteStoryStats, setAiWriteStoryStats] = useState(null);
  const [aiWriteStoryLoading, setAiWriteStoryLoading] = useState(false);
  const [showAiWriteStorySection, setShowAiWriteStorySection] = useState(false);
  const [aiWriteStoryJobRunning, setAiWriteStoryJobRunning] = useState(null);   // 'image' | 'finalize' | null
  const [aiWriteStoryJobResult, setAiWriteStoryJobResult] = useState(null);
  const [aiWriteStorySkipInProgress, setAiWriteStorySkipInProgress] = useState(null);

  // --- User WriteStory state ---
  const [userWriteStoryReviewQueue, setUserWriteStoryReviewQueue] = useState([]); // status = 1
  const [userWriteStoryImageQueue, setUserWriteStoryImageQueue] = useState([]);   // status = 3
  const [userWriteStoryReadyQueue, setUserWriteStoryReadyQueue] = useState([]);     // status = 4
  const [userWriteStoryRetryQueue, setUserWriteStoryRetryQueue] = useState([]);     // status = 6 (image_retry)
  const [userWriteStoryStats, setUserWriteStoryStats] = useState(null);
  const [userWriteStoryLoading, setUserWriteStoryLoading] = useState(false);
  const [showUserWriteStorySection, setShowUserWriteStorySection] = useState(false);
  const [userWriteStoryJobRunning, setUserWriteStoryJobRunning] = useState(null);
  const [userWriteStoryJobResult, setUserWriteStoryJobResult] = useState(null);
  const [userWriteStorySkipInProgress, setUserWriteStorySkipInProgress] = useState(null);

  // NEW: AI Story Generation state
  const [aiGenConfig, setAiGenConfig] = useState(null);   // { enabled, ideasPerBatch, threshold, schedule1, schedule2, consumerEnabled, consumerSchedule, consumerBatchSize, ... }
  const [aiGenStatus, setAiGenStatus] = useState(null);   // { availableIdeas, processingIdeas, usedIdeas, failedIdeas, totalIdeas, lastRunAt, lastBatchGenerated, lastRunStatus, lastRunMessage, schedulerRunning, consumerLastRunAt, consumerLastBatchConsumed, ... }
  const [aiGenLoading, setAiGenLoading] = useState(false);
  const [aiGenSaving, setAiGenSaving] = useState(false);
  const [aiGenRunning, setAiGenRunning] = useState(false);
  const [aiGenConsuming, setAiGenConsuming] = useState(false);
  const [aiGenResult, setAiGenResult] = useState(null);   // { ok: bool, summary: string } | null
  const [showAiGenSection, setShowAiGenSection] = useState(false);
  // Form drafts (so editing doesn't immediately push to backend)
  const [aiGenEnabled, setAiGenEnabled] = useState(true);
  const [aiGenIdeasPerBatch, setAiGenIdeasPerBatch] = useState(100);
  const [aiGenThreshold, setAiGenThreshold] = useState(100);
  const [aiGenSchedule1, setAiGenSchedule1] = useState('09:00');
  const [aiGenSchedule2, setAiGenSchedule2] = useState('21:00');
  // NEW: Consumer (drain → story_queue) form drafts
  const [aiGenConsumerEnabled, setAiGenConsumerEnabled] = useState(true);
  const [aiGenConsumerSchedule, setAiGenConsumerSchedule] = useState('*/5 * * * *');
  const [aiGenConsumerBatchSize, setAiGenConsumerBatchSize] = useState(1);
  const [aiGenConsumerMinAvailable, setAiGenConsumerMinAvailable] = useState(0);

  // NEW: LLM provider stats (Direct MiniMax primary + OpenRouter fallback)
  const [llmProviderStats, setLlmProviderStats] = useState(null);
  const [llmProviderStatsLoading, setLlmProviderStatsLoading] = useState(false);

  // NEW: Per-genre idea reservoir (idea generation admin triggers)
  const [genreReservoir, setGenreReservoir] = useState(null);     // { 'Science Fiction': 12, ... }
  const [genreReplenishInFlight, setGenreReplenishInFlight] = useState(new Set()); // Set of genres currently generating
  const [genreReplenishResult, setGenreReplenishResult] = useState(null); // last toast
  const [genreConsumeInFlight, setGenreConsumeInFlight] = useState(new Set());  // Set of genres currently consuming

  // NEW: Runtime Status (last 10 LLM failures + last 10 error outputs)
  const [llmFailures, setLlmFailures] = useState([]);
  const [errorOutputs, setErrorOutputs] = useState([]);
  const [llmFailuresLoading, setLlmFailuresLoading] = useState(false);
  const [showRuntimeStatusSection, setShowRuntimeStatusSection] = useState(false);

  const fetchPendingStories = useCallback(async () => {
    try {
      const params = {};
      if (selectedBatchname) {
        params.batchname = selectedBatchname;
      }
      params._t = Date.now();
      const response = await axios.get(`${API_URL}/admin/pending-stories`, {
        params,
        withCredentials: true
      });
      if (response.data.success) {
        setPendingStories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching pending stories:', error);
      setError('Failed to load pending stories');
    }
  }, [selectedBatchname]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stats`, {
        params: { _t: Date.now() },
        withCredentials: true
      });
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    }
  }, []);

  const fetchBatchnames = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/batchnames`, {
        params: { _t: Date.now() },
        withCredentials: true
      });
      if (response.data.success) {
        setBatchnames(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching batchnames:', error);
    }
  }, []);

  const fetchPrestageStories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/prestage-stories`, {
        params: { _t: Date.now() },
        withCredentials: true
      });
      if (response.data.success) {
        setPrestageStories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching prestage stories:', error);
    }
  }, []);

  const fetchStageStories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stage-stories`, {
        params: { _t: Date.now() },
        withCredentials: true
      });
      if (response.data.success) {
        setStageStories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stage stories:', error);
    }
  }, []);

  // NEW: Fetch rawstories (pending only by default)
  const fetchRawstories = useCallback(async () => {
    setRawstoryLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/rawstories`, {
        params: { _t: Date.now() },
        withCredentials: true
      });
      if (response.data.success) {
        setRawstories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching rawstories:', error);
    } finally {
      setRawstoryLoading(false);
    }
  }, []);

  // NEW: Fetch finalstage stories
  const fetchFinalstageStories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/finalstage-stories`, {
        params: { _t: Date.now() },
        withCredentials: true
      });
      if (response.data.success) {
        setFinalstageStories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching finalstage stories:', error);
    }
  }, []);

  // ============================================================
  // NEW: WriteStory Workflow fetchers — SEGREGATED into AI vs User.
  // Each fetcher pulls the stats + the status buckets relevant to its
  // source filter via ?source=ai|user on /api/admin/queue and the
  // matching /api/admin/queue/stats call.
  //
  // AI:   only status=3 (image) / 4 (ready) / 6 (retry). No status=1
  //       (review) because AI stories skip the reviewer.
  // User: all four status buckets.
  // ============================================================

  // --- AI WriteStory fetcher ---
  const fetchAiWriteStoryQueue = useCallback(async () => {
    setAiWriteStoryLoading(true);
    try {
      const [statsRes, imageRes, readyRes, retryRes] = await Promise.all([
        axios.get(`${API_URL}/admin/queue/stats`, { params: { source: 'ai', _t: Date.now() }, withCredentials: true }),
        axios.get(`${API_URL}/admin/queue`, { params: { source: 'ai', status: 3, limit: 50, _t: Date.now() }, withCredentials: true }),
        axios.get(`${API_URL}/admin/queue`, { params: { source: 'ai', status: 4, limit: 50, _t: Date.now() }, withCredentials: true }),
        axios.get(`${API_URL}/admin/queue`, { params: { source: 'ai', status: 6, limit: 50, _t: Date.now() }, withCredentials: true })
      ]);
      if (statsRes.data && statsRes.data.success) {
        setAiWriteStoryStats(statsRes.data.data);
      }
      if (imageRes.data && imageRes.data.success) {
        setAiWriteStoryImageQueue(imageRes.data.data || []);
      }
      if (readyRes.data && readyRes.data.success) {
        setAiWriteStoryReadyQueue(readyRes.data.data || []);
      }
      if (retryRes.data && retryRes.data.success) {
        setAiWriteStoryRetryQueue(retryRes.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching AI WriteStory queue:', error);
    } finally {
      setAiWriteStoryLoading(false);
    }
  }, []);

  // --- User WriteStory fetcher ---
  const fetchUserWriteStoryQueue = useCallback(async () => {
    setUserWriteStoryLoading(true);
    try {
      const [statsRes, reviewRes, imageRes, readyRes, retryRes] = await Promise.all([
        axios.get(`${API_URL}/admin/queue/stats`, { params: { source: 'user', _t: Date.now() }, withCredentials: true }),
        axios.get(`${API_URL}/admin/queue`, { params: { source: 'user', status: 1, limit: 50, _t: Date.now() }, withCredentials: true }),
        axios.get(`${API_URL}/admin/queue`, { params: { source: 'user', status: 3, limit: 50, _t: Date.now() }, withCredentials: true }),
        axios.get(`${API_URL}/admin/queue`, { params: { source: 'user', status: 4, limit: 50, _t: Date.now() }, withCredentials: true }),
        axios.get(`${API_URL}/admin/queue`, { params: { source: 'user', status: 6, limit: 50, _t: Date.now() }, withCredentials: true })
      ]);
      if (statsRes.data && statsRes.data.success) {
        setUserWriteStoryStats(statsRes.data.data);
      }
      if (reviewRes.data && reviewRes.data.success) {
        setUserWriteStoryReviewQueue(reviewRes.data.data || []);
      }
      if (imageRes.data && imageRes.data.success) {
        setUserWriteStoryImageQueue(imageRes.data.data || []);
      }
      if (readyRes.data && readyRes.data.success) {
        setUserWriteStoryReadyQueue(readyRes.data.data || []);
      }
      if (retryRes.data && retryRes.data.success) {
        setUserWriteStoryRetryQueue(retryRes.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching User WriteStory queue:', error);
    } finally {
      setUserWriteStoryLoading(false);
    }
  }, []);

  // NEW: Trigger a single pipeline stage on demand (segregated).
  // stage:  'review' | 'image' | 'finalize'
  // source: 'ai' | 'user'  -> selects the right state setters and refetch fn.
  const handleWriteStoryJob = async (stage, source) => {
    const setJobRunning = source === 'ai' ? setAiWriteStoryJobRunning : setUserWriteStoryJobRunning;
    const setJobResult  = source === 'ai' ? setAiWriteStoryJobResult  : setUserWriteStoryJobResult;
    const refetchQueue  = source === 'ai' ? fetchAiWriteStoryQueue  : fetchUserWriteStoryQueue;
    setError(null);
    setMessage(null);
    setJobRunning(stage);
    setJobResult(null);
    try {
      const endpoint =
        stage === 'review'   ? `${API_URL}/admin/queue/process-review`  :
        stage === 'image'    ? `${API_URL}/admin/queue/process-images`  :
                               `${API_URL}/admin/queue/finalize`;
      // Default limits: image capped at 3 by the server (HF 1-min mutex means
      // a batch of 3 takes ~3-5 min); review/finalize default 20.
      const limit = stage === 'image' ? 3 : 20;
      const response = await axios.post(endpoint, { limit }, { withCredentials: true });
      if (response.data && response.data.success) {
        const data = response.data.data || {};
        let summary = '';
        if (stage === 'review') {
          summary = `🔍 Review: ${data.accepted || 0} accepted, ${data.rejected || 0} rejected (smut), ${data.errors || 0} skipped`;
        } else if (stage === 'image') {
          const byTier = data.by_tier || {};
          const tierParts = [];
          if (byTier.gemini_direct)  tierParts.push(`🤖 Gemini direct: ${byTier.gemini_direct}`);
          if (byTier.nano_banana)   tierParts.push(`🍌 Nano Banana: ${byTier.nano_banana}`);
          if (byTier.pollinations)  tierParts.push(`🌸 Pollinations: ${byTier.pollinations}`);
          if (byTier.openrouter_other) tierParts.push(`🅾️ OpenRouter: ${byTier.openrouter_other}`);
          const tierSummary = tierParts.length > 0 ? ` (${tierParts.join(', ')})` : '';
          const retryInfo = (data.retry_scheduled || 0) > 0
            ? `, ${data.retry_scheduled} scheduled for retry`
            : '';
          const rejectInfo = (data.auto_rejected || 0) > 0
            ? `, ${data.auto_rejected} auto-rejected`
            : '';
          summary = `🎨 Images: ${data.succeeded || 0} generated, ${data.failed || 0} failed${tierSummary}${retryInfo}${rejectInfo}`;
        } else {
          summary = `📚 Finalize: ${data.published || 0} published, ${data.failed || 0} failed, ${data.competitionEntries || 0} competition entries`;
        }
        setJobResult({ stage, ok: true, summary, data });
        setMessage(summary);
        await refetchQueue();
      } else {
        setJobResult({ stage, ok: false, summary: 'Job returned no success' });
      }
    } catch (error) {
      console.error(`Error running WriteStory ${stage} (source=${source}):`, error);
      const msg = error.response?.data?.error || error.message || 'Unknown error';
      setJobResult({ stage, ok: false, summary: `❌ ${msg}` });
      setError(`WriteStory ${stage} failed: ${msg}`);
    } finally {
      setJobRunning(null);
    }
  };

  // NEW: Skip image generation for a single story (segregated).
  // Used when all backends have failed and the admin wants to move
  // the story forward anyway. The `source` param selects which
  // state setters and refetch fn to use.
  const handleWriteStorySkipImage = async (queueId, id, source) => {
    if (!window.confirm(`Skip image generation for ${queueId}? The story will be published with a placeholder cover.`)) {
      return;
    }
    const setSkipInProgress = source === 'ai' ? setAiWriteStorySkipInProgress : setUserWriteStorySkipInProgress;
    const refetchQueue       = source === 'ai' ? fetchAiWriteStoryQueue       : fetchUserWriteStoryQueue;
    setSkipInProgress(id);
    setError(null);
    setMessage(null);
    try {
      const response = await axios.post(`${API_URL}/admin/queue/skip-image/${id}`, {}, { withCredentials: true });
      if (response.data && response.data.success) {
        setMessage(`⏭️ Skipped image for ${queueId}; story is now ready to publish.`);
        await refetchQueue();
      } else {
        setError(`Skip failed: ${response.data?.error || 'unknown error'}`);
      }
    } catch (error) {
      console.error('Error skipping image:', error);
      setError(`Skip failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setSkipInProgress(null);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPendingStories(), fetchStats(), fetchBatchnames()]);
      setLoading(false);
    };
    loadData();
  }, [fetchPendingStories, fetchStats, fetchBatchnames]);

  useEffect(() => {
    if (showPrestageSection) {
      Promise.all([fetchPrestageStories(), fetchStageStories()]);
    }
  }, [showPrestageSection, fetchPrestageStories, fetchStageStories]);

  // NEW: Load rawstories when section is shown
  useEffect(() => {
    if (showRawstorySection) {
      fetchRawstories();
    }
  }, [showRawstorySection, fetchRawstories]);

  // NEW: Load finalstage stories when section is shown
  useEffect(() => {
    if (showFinalstageSection) {
      fetchFinalstageStories();
    }
  }, [showFinalstageSection, fetchFinalstageStories]);

  // NEW: Load AI / User WriteStory queues when their respective sections are shown.
  // Also re-poll every 10s while open so the admin sees fresh counts without
  // hitting Refresh manually.
  useEffect(() => {
    if (showAiWriteStorySection) {
      fetchAiWriteStoryQueue();
      const interval = setInterval(fetchAiWriteStoryQueue, 10000);
      return () => clearInterval(interval);
    }
  }, [showAiWriteStorySection, fetchAiWriteStoryQueue]);

  useEffect(() => {
    if (showUserWriteStorySection) {
      fetchUserWriteStoryQueue();
      const interval = setInterval(fetchUserWriteStoryQueue, 10000);
      return () => clearInterval(interval);
    }
  }, [showUserWriteStorySection, fetchUserWriteStoryQueue]);

  // NEW: AI Story Generation fetchers
  // Pulls current config (enabled flag, batch size, threshold, two schedules)
  // and live status (counts, last-run info, scheduler health) from
  // /api/admin/generation/{config,status}.
  const fetchAiGenData = useCallback(async () => {
    setAiGenLoading(true);
    try {
      const [configRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/admin/generation/config`, { params: { _t: Date.now() }, withCredentials: true }),
        axios.get(`${API_URL}/admin/generation/status`, { params: { _t: Date.now() }, withCredentials: true })
      ]);
      if (configRes.data && configRes.data.success) {
        const cfg = configRes.data.data;
        setAiGenConfig(cfg);
        // Seed generation form drafts from backend values
        setAiGenEnabled(!!cfg.enabled);
        setAiGenIdeasPerBatch(cfg.ideasPerBatch || 100);
        setAiGenThreshold(cfg.threshold ?? 100);
        setAiGenSchedule1(cfg.schedule1 || '09:00');
        setAiGenSchedule2(cfg.schedule2 || '21:00');
        // NEW: Seed consumer (drain → story_queue) form drafts
        setAiGenConsumerEnabled(!!cfg.consumerEnabled);
        setAiGenConsumerSchedule(cfg.consumerSchedule || '*/5 * * * *');
        setAiGenConsumerBatchSize(cfg.consumerBatchSize || 1);
        setAiGenConsumerMinAvailable(cfg.consumerMinAvailable ?? 0);
      }
      if (statusRes.data && statusRes.data.success) {
        setAiGenStatus(statusRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching AI Generation data:', error);
      setError('Failed to load AI Story Generation data');
    } finally {
      setAiGenLoading(false);
    }
  }, []);

  // NEW: Fetch LLM provider stats (MiniMax direct + OpenRouter fallback counters)
  const fetchLlmProviderStats = useCallback(async () => {
    setLlmProviderStatsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/llm-provider-stats`, {
        params: { _t: Date.now() }
      });
      if (response.data?.success) {
        setLlmProviderStats(response.data.data);
      }
    } catch (error) {
      // Silent — don't surface provider stats errors to the user
      console.error('Error fetching LLM provider stats:', error);
    } finally {
      setLlmProviderStatsLoading(false);
    }
  }, []);

  // NEW: Fetch per-genre reservoir counts. Uses the existing /status
  // endpoint's `availableByGenre` field — no new endpoint needed.
  const fetchGenreReservoir = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/generation/status`, {
        params: { _t: Date.now() },
        withCredentials: true
      });
      if (response.data?.success && response.data.data) {
        // The status endpoint doesn't currently expose per-genre counts;
        // fall back to fetching via /admin/generation/status and inferring
        // from the existing aggregate counts. If the server adds a
        // dedicated per-genre endpoint, swap in here.
        // For now we use the existing 'availableByGenre' if present:
        if (response.data.data.availableByGenre) {
          setGenreReservoir(response.data.data.availableByGenre);
        }
      }
    } catch (error) {
      console.error('Error fetching genre reservoir:', error);
    }
  }, []);

  // NEW: Trigger idea generation for a SINGLE genre.
  const handleReplenishGenre = async (genre) => {
    setError(null);
    setMessage(null);
    setGenreReplenishResult(null);
    setGenreReplenishInFlight(prev => new Set([...prev, genre]));

    try {
      const response = await axios.post(`${API_URL}/admin/generation/replenish-genre`,
        { genre },
        { withCredentials: true }
      );
      if (response.data?.success) {
        const d = response.data.data || {};
        if (d.skipped) {
          setMessage(`⏭️ ${genre}: ${d.reason} — skipped`);
        } else {
          setMessage(`✅ ${genre}: generated ${d.saved ?? 0}/${d.generated ?? 0} ideas (model: ${d.model ?? '?'})`);
        }
        setGenreReplenishResult({ ok: true, summary: `${genre} replenish complete` });
        // Refresh reservoir counts immediately.
        await fetchGenreReservoir();
      } else {
        setError(`${genre}: ${response.data?.error || 'unknown error'}`);
        setGenreReplenishResult({ ok: false, summary: response.data?.error || 'unknown error' });
      }
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.error || error.message;
      if (status === 409) {
        setError(`⏳ ${genre} is already running — try again in a moment`);
      } else if (status === 400) {
        setError(`❌ ${genre}: ${msg}`);
      } else {
        setError(`❌ ${genre}: ${msg}`);
      }
      setGenreReplenishResult({ ok: false, summary: msg });
    } finally {
      setGenreReplenishInFlight(prev => {
        const next = new Set(prev);
        next.delete(genre);
        return next;
      });
    }
  };

  // NEW: Consume up to `count` ideas (default 10) from a SINGLE genre.
  // Calls POST /api/admin/generation/consume-genre. Each call consumes
  // one idea via consumer.consumeNByGenre(genre, count). On success,
  // refreshes the per-genre reservoir counts so the "Available" column
  // drops immediately. Toast message shows consumed/failed/skipped.
  const handleConsumeGenre = async (genre, count = 10) => {
    setError(null);
    setMessage(null);
    setGenreReplenishResult(null);
    setGenreConsumeInFlight(prev => new Set([...prev, genre]));

    try {
      const response = await axios.post(`${API_URL}/admin/generation/consume-genre`,
        { genre, count },
        { withCredentials: true }
      );
      if (response.data?.success) {
        const d = response.data.data || {};
        const consumed = d.consumed ?? 0;
        const failed = d.failed ?? 0;
        const slotsSkipped = d.slotsSkipped ?? 0;
        setMessage(`🔥 ${genre}: consumed ${consumed}/${d.attempted ?? count} ideas (${failed} failed, ${slotsSkipped} slots empty)`);
        setGenreReplenishResult({ ok: true, summary: `${genre} consume: ${consumed} ideas generated` });
        await fetchGenreReservoir();
      } else {
        const msg = response.data?.error || 'unknown error';
        setError(`❌ ${genre} consume: ${msg}`);
        setGenreReplenishResult({ ok: false, summary: msg });
      }
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.error || error.message;
      if (status === 409) {
        setError(`⏳ ${genre} consume is already running — try again in a moment`);
      } else {
        setError(`❌ ${genre} consume: ${msg}`);
      }
      setGenreReplenishResult({ ok: false, summary: msg });
    } finally {
      setGenreConsumeInFlight(prev => {
        const next = new Set(prev);
        next.delete(genre);
        return next;
      });
    }
  };

  // NEW: Fetch last 10 LLM failures + last 10 error outputs.
  const fetchRuntimeStatus = useCallback(async () => {
    setLlmFailuresLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/llm-failures`, {
        params: { _t: Date.now() },
        withCredentials: true
      });
      if (response.data?.success && response.data.data) {
        setLlmFailures(response.data.data.llmFailures || []);
        setErrorOutputs(response.data.data.errorOutputs || []);
      }
    } catch (error) {
      console.error('Error fetching runtime status:', error);
    } finally {
      setLlmFailuresLoading(false);
    }
  }, []);

  // NEW: Save config (5 generation + 4 consumer fields) → /api/admin/generation/config (POST)
  // The server validates values, restarts the cron jobs if any schedule
  // changed, and returns the new config.
  const handleSaveAiGenConfig = async () => {
    setAiGenSaving(true);
    setAiGenResult(null);
    setError(null);
    setMessage(null);
    try {
      // ----- Generation validation -----
      const n = parseInt(aiGenIdeasPerBatch, 10);
      if (isNaN(n) || n < 1 || n > 500) {
        setError('Ideas per Batch must be 1-500');
        setAiGenSaving(false);
        return;
      }
      const t = parseInt(aiGenThreshold, 10);
      if (isNaN(t) || t < 0 || t > 10000) {
        setError('Replenishment Threshold must be 0-10000');
        setAiGenSaving(false);
        return;
      }
      const timeRe = /^\d{1,2}:\d{2}$/;
      if (!timeRe.test(aiGenSchedule1) || !timeRe.test(aiGenSchedule2)) {
        setError('Schedules must be in HH:MM format');
        setAiGenSaving(false);
        return;
      }

      // ----- Consumer validation -----
      const cb = parseInt(aiGenConsumerBatchSize, 10);
      if (isNaN(cb) || cb < 1 || cb > 20) {
        setError('Consumer Batch Size must be 1-20');
        setAiGenSaving(false);
        return;
      }
      const cma = parseInt(aiGenConsumerMinAvailable, 10);
      if (isNaN(cma) || cma < 0 || cma > 10000) {
        setError('Consumer Min Available must be 0-10000');
        setAiGenSaving(false);
        return;
      }
      // Light client-side cron validation (server validates with node-cron anyway)
      if (typeof aiGenConsumerSchedule !== 'string' || aiGenConsumerSchedule.trim().length < 9) {
        setError('Consumer Schedule must look like a cron expression (e.g. "*/5 * * * *")');
        setAiGenSaving(false);
        return;
      }

      const response = await axios.post(`${API_URL}/admin/generation/config`, {
        enabled: !!aiGenEnabled,
        ideasPerBatch: n,
        threshold: t,
        schedule1: aiGenSchedule1,
        schedule2: aiGenSchedule2,
        consumerEnabled: !!aiGenConsumerEnabled,
        consumerSchedule: aiGenConsumerSchedule.trim(),
        consumerBatchSize: cb,
        consumerMinAvailable: cma
      }, { withCredentials: true });

      if (response.data && response.data.success) {
        setAiGenConfig(response.data.data);
        setMessage('✅ AI Story Generation configuration saved (generation + consumer)');
        // Refresh status so any scheduler-restart side-effects are visible
        await fetchAiGenData();
      } else {
        setError(`Save failed: ${response.data?.error || 'unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving AI Generation config:', error);
      setError(`Save failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setAiGenSaving(false);
    }
  };

  // NEW: Generate Ideas Now → /api/admin/generation/run (POST)
  // Triggers the SAME scheduler.replenish() the cron uses. May take
  // 1-3 minutes for a 100-concept batch; disable the button while running
  // and surface the result message.
  const handleGenerateIdeasNow = async () => {
    if (!window.confirm(`Generate ${aiGenConfig?.ideasPerBatch || 100} new story ideas now? This calls the same service as the daily cron and may take 1-3 minutes.`)) {
      return;
    }
    setAiGenRunning(true);
    setAiGenResult(null);
    setError(null);
    setMessage(null);
    try {
      const response = await axios.post(`${API_URL}/admin/generation/run`, {}, { withCredentials: true });
      if (response.data && response.data.success) {
        const data = response.data.data || {};
        let summary;
        if (data.saved !== undefined) {
          summary = `✅ Generation complete: ${data.saved}/${data.target || '?'} ideas saved (model: ${data.model || 'unknown'})`;
        } else if (data.skipped) {
          summary = `⏭️ Skipped: ${data.reason}${data.available !== undefined ? ` (${data.available} available)` : ''}`;
        } else if (data.error) {
          summary = `❌ Generation failed: ${data.error}`;
        } else {
          summary = '✅ Generation complete';
        }
        setAiGenResult({ ok: true, summary, data });
        setMessage(summary);
        // Refresh status so the new "Last Generation Time" / "Last Batch Size" appear
        await fetchAiGenData();
      } else {
        setAiGenResult({ ok: false, summary: response.data?.error || 'Generation failed' });
        setError(`Generation failed: ${response.data?.error || 'unknown error'}`);
      }
    } catch (error) {
      console.error('Error running AI Generation:', error);
      const msg = error.response?.data?.error || error.message;
      setAiGenResult({ ok: false, summary: `❌ ${msg}` });
      setError(`Generation failed: ${msg}`);
    } finally {
      setAiGenRunning(false);
    }
  };

  // NEW: Consume Ideas Now → /api/admin/generation/consume (POST)
  // Triggers scheduler.consumeNow() (same code path as the consumer cron):
  //   1. picks up to consumerBatchSize AVAILABLE ideas
  //   2. generates a full story via Paraphraser (minimax/minimax-m3)
  //   3. inserts into story_queue with status=1, author='AI StoryMeal'
  //   4. marks each idea USED on success / FAILED on error
  // The EXISTING StoryQueueReviewer → ImageGenerator → Finalizer then
  // processes the inserted rows automatically — no new workflows.
  const handleConsumeIdeasNow = async () => {
    const count = aiGenConfig?.consumerBatchSize || 1;
    if (!window.confirm(`Consume up to ${count} AVAILABLE idea(s) now? Each idea is sent to MiniMax for full-story generation (1-2 min per idea). The existing story_queue pipeline picks them up afterwards.`)) {
      return;
    }
    setAiGenConsuming(true);
    setAiGenResult(null);
    setError(null);
    setMessage(null);
    try {
      const response = await axios.post(`${API_URL}/admin/generation/consume`, {}, { withCredentials: true });
      if (response.data && response.data.success) {
        const data = response.data.data || {};
        let summary;
        if (data.skipped) {
          summary = `⏭️ Skipped: ${data.reason}${data.available !== undefined ? ` (${data.available} available)` : ''}`;
        } else if (data.consumed !== undefined) {
          const failed = data.failed || 0;
          summary = failed > 0
            ? `✅ Consumed ${data.consumed}, failed ${failed} (marked FAILED)`
            : `✅ Consumed ${data.consumed} idea(s) → inserted into story_queue`;
        } else if (data.error) {
          summary = `❌ Consumption failed: ${data.error}`;
        } else {
          summary = '✅ Consumption complete';
        }
        setAiGenResult({ ok: true, summary, data });
        setMessage(summary);
        // Refresh so available count + last consumer run update
        await fetchAiGenData();
        // Also refresh the WriteStory queue (newly inserted rows show up as status=1)
        if (typeof fetchWriteStoryQueue === 'function') {
          await fetchWriteStoryQueue();
        }
      } else {
        setAiGenResult({ ok: false, summary: response.data?.error || 'Consumption failed' });
        setError(`Consumption failed: ${response.data?.error || 'unknown error'}`);
      }
    } catch (error) {
      console.error('Error running consumer:', error);
      const msg = error.response?.data?.error || error.message;
      setAiGenResult({ ok: false, summary: `❌ ${msg}` });
      setError(`Consumption failed: ${msg}`);
    } finally {
      setAiGenConsuming(false);
    }
  };

  // NEW: Load AI Generation data + LLM provider stats when the section is opened.
  // Poll provider stats every 10s while the section is open.
  // Also polls per-genre reservoir every 10s while the section is open.
  useEffect(() => {
    if (showAiGenSection) {
      fetchAiGenData();
      fetchLlmProviderStats();
      fetchGenreReservoir();
      const interval = setInterval(() => {
        fetchLlmProviderStats();
        fetchGenreReservoir();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [showAiGenSection, fetchAiGenData, fetchLlmProviderStats, fetchGenreReservoir]);

  // NEW: Runtime Status polling — every 10s while section is open.
  useEffect(() => {
    if (showRuntimeStatusSection) {
      fetchRuntimeStatus();
      const interval = setInterval(fetchRuntimeStatus, 10000);
      return () => clearInterval(interval);
    }
  }, [showRuntimeStatusSection, fetchRuntimeStatus]);

  const handleApprove = async (id) => {
    setActionLoading(id);
    setError(null);
    setMessage(null);
    try {
      const response = await axios.post(`${API_URL}/admin/approve-story/${id}`, {}, { withCredentials: true });
      if (response.data.success) {
        setMessage('Story approved and published successfully!');
        setPendingStories(prev => prev.filter(story => story.id !== id));
        fetchStats();
      } else if (response.data.auto_rejected) {
        // FIX: Handle auto-rejection of unparaphrased stories
        setMessage(`🗑️ ${response.data.message}`);
        setPendingStories(prev => prev.filter(story => story.id !== id));
        fetchStats();
      }
    } catch (error) {
      console.error('Error approving story:', error);
      setError(error.response?.data?.error || 'Failed to approve story');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Are you sure you want to reject and delete this story?')) {
      return;
    }
    setActionLoading(id);
    setError(null);
    setMessage(null);
    try {
      const response = await axios.delete(`${API_URL}/admin/reject-story/${id}`, { withCredentials: true });
      if (response.data.success) {
        setMessage('Story rejected and deleted.');
        setPendingStories(prev => prev.filter(story => story.id !== id));
        fetchStats();
      }
    } catch (error) {
      console.error('Error rejecting story:', error);
      setError(error.response?.data?.error || 'Failed to reject story');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveAll = async () => {
    setConfirmAction(null);
    setError(null);
    setMessage(null);
    setActionLoading('bulk');
    try {
      const response = await axios.post(`${API_URL}/admin/approve-all-stories`,
        { batchname: selectedBatchname || undefined },
        { withCredentials: true }
      );
      if (response.data.success) {
        setMessage(`✅ ${response.data.message}`);
        setPendingStories([]);
        fetchStats();
      }
    } catch (error) {
      console.error('Error approving all stories:', error);
      setError(error.response?.data?.error || 'Failed to approve all stories');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectAll = async () => {
    setConfirmAction(null);
    setError(null);
    setMessage(null);
    setActionLoading('bulk');
    try {
      const response = await axios.post(`${API_URL}/admin/reject-all-stories`,
        { batchname: selectedBatchname || undefined },
        { withCredentials: true }
      );
      if (response.data.success) {
        setMessage(`🗑️ ${response.data.message}`);
        setPendingStories([]);
        fetchStats();
      }
    } catch (error) {
      console.error('Error rejecting all stories:', error);
      setError(error.response?.data?.error || 'Failed to reject all stories');
    } finally {
      setActionLoading(null);
    }
  };

  const handleProcessPrestage = async () => {
    setError(null);
    setMessage(null);
    setActionLoading('process-prestage');
    try {
      const response = await axios.post(`${API_URL}/admin/process-prestage`,
        { limit: 20 },
        { withCredentials: true }
      );
      if (response.data.success) {
        setMessage(`✅ Processed ${response.data.data.validated} prestage stories, ${response.data.data.storiesAccepted} accepted`);
        fetchPrestageStories();
        fetchStats();
      }
    } catch (error) {
      console.error('Error processing prestage:', error);
      setError(error.response?.data?.error || 'Failed to process prestage stories');
    } finally {
      setActionLoading(null);
    }
  };

  const handleProcessStage = async () => {
    setError(null);
    setMessage(null);
    setActionLoading('process-stage');
    try {
      const response = await axios.post(`${API_URL}/admin/process-stage`,
        { limit: 20, batchname: selectedBatchname || undefined },
        { withCredentials: true }
      );
      if (response.data.success) {
        setMessage(`✅ Paraphrased ${response.data.data.processed} stage stories (${response.data.data.batchname})`);
        fetchStageStories();
        fetchStats();
      }
    } catch (error) {
      console.error('Error processing stage:', error);
      setError(error.response?.data?.error || 'Failed to process stage stories');
    } finally {
      setActionLoading(null);
    }
  };

  const handleProcessFinalstage = async () => {
    setError(null);
    setMessage(null);
    setActionLoading('process-finalstage');
    try {
      const response = await axios.post(`${API_URL}/admin/process-finalstage`,
        { limit: 20 },
        { withCredentials: true }
      );
      if (response.data.success) {
        setMessage(`✅ LLM reviewed ${response.data.data.autoApproved} stories (${response.data.data.needsAdmin} need admin review)`);
        fetchStageStories();
        fetchPendingStories();
        fetchStats();
      }
    } catch (error) {
      console.error('Error processing finalstage:', error);
      setError(error.response?.data?.error || 'Failed to process finalstage stories');
    } finally {
      setActionLoading(null);
    }
  };

  // NEW: Process rawstories with configurable limit
  const handleProcessRawstories = async (limit) => {
    setError(null);
    setMessage(null);
    setActionLoading(`process-rawstory-${limit}`);
    setShowRawstoryDropdown(false);
    try {
      const response = await axios.post(`${API_URL}/admin/process-rawstories`,
        { limit },
        { withCredentials: true }
      );
      if (response.data.success) {
        const data = response.data.data;
        setMessage(`✅ Processed ${data.accepted} raw stories (${data.autoPublished || 0} auto-published by paid LLM, ${data.failed} failed)`);
        fetchRawstories();
        fetchStats();
      }
    } catch (error) {
      console.error('Error processing rawstories:', error);
      setError(error.response?.data?.error || 'Failed to process rawstories');
    } finally {
      setActionLoading(null);
    }
  };

  // NEW: Scrape-only pipeline - scrape websites and save to rawstory table only
  const handleScrapeOnly = async () => {
    if (!window.confirm('Start scrape-only pipeline? This will scrape websites and save to rawstory table only (no processing).')) {
      return;
    }
    setError(null);
    setMessage(null);
    setScrapeOnlyLoading(true);
    setShowRawstoryDropdown(false);
    try {
      const response = await axios.post(`${API_URL}/admin/scrape-only`,
        {},
        { withCredentials: true }
      );
      if (response.data.success) {
        const data = response.data.data;
        setMessage(`🔍 Scrape-only complete: ${data.success} stories saved to rawstory (${data.failed} failed)`);
        fetchRawstories();
        fetchStats();
      }
    } catch (error) {
      console.error('Error in scrape-only pipeline:', error);
      setError(error.response?.data?.error || 'Failed to run scrape-only pipeline');
    } finally {
      setScrapeOnlyLoading(false);
    }
  };

  const fetchPublishedStories = useCallback(async (search = '', offset = 0) => {
    setPublishedLoading(true);
    try {
      const params = { limit: 20, offset, _t: Date.now() };
      if (search) params.search = search;
      const response = await axios.get(`${API_URL}/admin/stories`, {
        params,
        withCredentials: true
      });
      if (response.data.success) {
        setPublishedStories(response.data.data.stories);
        setPublishedTotal(response.data.data.total);
        setPublishedOffset(offset);
      }
    } catch (error) {
      console.error('Error fetching published stories:', error);
      setError('Failed to load published stories');
    } finally {
      setPublishedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showPublishedSection) {
      fetchPublishedStories(publishedSearch, 0);
    }
  }, [showPublishedSection, fetchPublishedStories]);

  const handleDeletePublishedStory = async (storyId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This cannot be undone.`)) {
      return;
    }
    setDeletingStoryId(storyId);
    setError(null);
    setMessage(null);
    try {
      const response = await axios.delete(`${API_URL}/admin/stories/${storyId}`, { withCredentials: true });
      if (response.data.success) {
        setMessage(`🗑️ "${title}" deleted successfully`);
        setPublishedStories(prev => prev.filter(s => s.story_id !== storyId));
        setPublishedTotal(prev => prev - 1);
      }
    } catch (error) {
      console.error('Error deleting story:', error);
      setError(error.response?.data?.error || 'Failed to delete story');
    } finally {
      setDeletingStoryId(null);
    }
  };

  const handlePublishedSearch = () => {
    fetchPublishedStories(publishedSearch, 0);
  };

  const handleDeleteAllPrestage = async () => {
    if (!window.confirm('Are you sure you want to delete ALL prestage stories? This cannot be undone.')) {
      return;
    }
    setError(null);
    setMessage(null);
    setActionLoading('delete-prestage');
    try {
      const response = await axios.delete(`${API_URL}/admin/delete-all-prestage`, { withCredentials: true });
      if (response.data.success) {
        setMessage(`🗑️ ${response.data.message}`);
        fetchPrestageStories();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting prestage:', error);
      setError(error.response?.data?.error || 'Failed to delete prestage stories');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleExpand = (id) => {
    setExpandedStory(expandedStory === id ? null : id);
  };

  // Extract plain text from HTML content
  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  };

  // Truncate text
  const truncate = (text, maxLength = 200) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Get word count
  const wordCount = (text) => {
    if (!text) return 0;
    return text.split(/\s+/).filter(w => w.length > 0).length;
  };

  if (!user || user.email !== 'sandip.kumar.kol@gmail.com') {
    return (
      <div className="admin-dashboard">
        <div className="admin-error">
          <h2>🔒 Access Denied</h2>
          <p>You do not have admin privileges to access this page.</p>
          <button className="admin-back-btn" onClick={onBack}>← Back to Home</button>
        </div>
      </div>
    );
  }

  // Defense-in-depth: even though the whole dashboard is admin-gated above,
  // re-check before rendering the top stats row. Mirrors the same constant
  // used by `requireAdmin` on the backend so future refactors stay consistent.
  const isAdmin = user?.email === 'sandip.kumar.kol@gmail.com';

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <button className="admin-back-btn" onClick={onBack}>← Back to Home</button>
        <h1>🛡️ Admin Dashboard</h1>
        <div className="admin-user-badge">
          👤 {user.pet_name || user.email}
        </div>
      </div>

      {/* Stats Cards - Enhanced with rawstory and auto-published counts.
          Gated by `isAdmin` as defense-in-depth: even if the early-return
          above is bypassed in a future refactor, these counts (pending
          approvals, auto-published counts, batches) will not leak. */}
      {stats && isAdmin && (
        <div className="admin-stats">
          <div className="admin-stat-card pending">
            <div className="admin-stat-icon">⏳</div>
            <div className="admin-stat-info">
              <span className="admin-stat-number">{stats.pending_approval}</span>
              <span className="admin-stat-label">Pending Admin Review</span>
            </div>
          </div>
          <div className="admin-stat-card approved">
            <div className="admin-stat-icon">🤖</div>
            <div className="admin-stat-info">
              <span className="admin-stat-number">{stats.auto_approved || 0}</span>
              <span className="admin-stat-label">Auto-Approved by LLM</span>
            </div>
          </div>
          <div className="admin-stat-card auto-published">
            <div className="admin-stat-icon">💎</div>
            <div className="admin-stat-info">
              <span className="admin-stat-number">{stats.auto_published_paid || 0}</span>
              <span className="admin-stat-label">Auto-Published (Paid LLM)</span>
            </div>
          </div>
          <div className="admin-stat-card rawstory">
            <div className="admin-stat-icon">📥</div>
            <div className="admin-stat-info">
              <span className="admin-stat-number">{stats.rawstory_pending || 0}</span>
              <span className="admin-stat-label">Raw Stories Pending</span>
            </div>
          </div>
          <div className="admin-stat-card prestage">
            <div className="admin-stat-icon">📋</div>
            <div className="admin-stat-info">
              <span className="admin-stat-number">{stats.prestage_pending}</span>
              <span className="admin-stat-label">Pre-stage Pending</span>
            </div>
          </div>
          <div className="admin-stat-card processed">
            <div className="admin-stat-icon">✅</div>
            <div className="admin-stat-info">
              <span className="admin-stat-number">{stats.stage_processed}</span>
              <span className="admin-stat-label">Stage Processed</span>
            </div>
          </div>
          <div className="admin-stat-card batches">
            <div className="admin-stat-icon">📦</div>
            <div className="admin-stat-info">
              <span className="admin-stat-number">{stats.total_batches || 0}</span>
              <span className="admin-stat-label">Total Batches</span>
            </div>
          </div>
        </div>
      )}

      {/* Batch Name Filter */}
      <div className="admin-filters">
        <label>Filter by Batch:</label>
        <select
          value={selectedBatchname}
          onChange={(e) => setSelectedBatchname(e.target.value)}
          className="admin-batch-select"
        >
          <option value="">All Batches</option>
          {/* FIX: Add migration batch option for stories with NULL batchname */}
          <option value="migration">🔧 Migration Batch (NULL batchname)</option>
          {batchnames.map(bn => (
            <option key={bn} value={bn}>{bn}</option>
          ))}
        </select>
        <button
          className="admin-refresh-btn"
          onClick={() => { fetchPendingStories(); fetchStats(); }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Messages */}
      {message && (
        <div className="admin-message success">
          <span>✅</span> {message}
          <button className="admin-message-close" onClick={() => setMessage(null)}>✕</button>
        </div>
      )}
      {error && (
        <div className="admin-message error">
          <span>❌</span> {error}
          <button className="admin-message-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Bulk Actions */}
      {pendingStories.length > 0 && (
        <div className="admin-bulk-actions">
          <span className="admin-bulk-count">{pendingStories.length} stories pending</span>
          <div className="admin-bulk-buttons">
            <button
              className="admin-approve-all-btn"
              onClick={() => setConfirmAction('approve_all')}
              disabled={actionLoading === 'bulk'}
            >
              {actionLoading === 'bulk' ? '⏳ Processing...' : '✅ Approve All'}
            </button>
            <button
              className="admin-reject-all-btn"
              onClick={() => setConfirmAction('reject_all')}
              disabled={actionLoading === 'bulk'}
            >
              {actionLoading === 'bulk' ? '⏳' : '🗑️ Reject All'}
            </button>
          </div>
        </div>
      )}

      {/* NEW: Raw Stories Section */}
      <div className="admin-section" style={{ marginTop: '30px' }}>
        <div className="admin-section-header" onClick={() => setShowRawstorySection(!showRawstorySection)} style={{ cursor: 'pointer' }}>
          <h2>📥 Raw Stories ({stats?.rawstory_pending || 0} pending)</h2>
          <span>{showRawstorySection ? '▲ Hide' : '▼ Show'}</span>
        </div>

        {showRawstorySection && (
          <>
            <div className="admin-bulk-actions">
              {/* Dropdown for pipeline actions */}
              <div className="admin-dropdown-container">
                <button
                  className="admin-approve-all-btn admin-dropdown-toggle"
                  onClick={() => setShowRawstoryDropdown(!showRawstoryDropdown)}
                  disabled={scrapeOnlyLoading || actionLoading?.startsWith('process-rawstory')}
                >
                  {scrapeOnlyLoading || actionLoading?.startsWith('process-rawstory')
                    ? '⏳ Processing...'
                    : '⚙️ Pipeline Actions ▼'}
                </button>
                {showRawstoryDropdown && (
                  <div className="admin-dropdown-menu">
                    <div className="admin-dropdown-header">Scrape (fill rawstory table)</div>
                    <button
                      className="admin-dropdown-item"
                      onClick={handleScrapeOnly}
                      disabled={scrapeOnlyLoading}
                    >
                      {scrapeOnlyLoading ? '⏳ Scraping...' : '🔍 Scrape Only (no processing)'}
                    </button>

                    <div className="admin-dropdown-divider"></div>

                    <div className="admin-dropdown-header">Process (from rawstory table)</div>
                    <button
                      className="admin-dropdown-item"
                      onClick={() => handleProcessRawstories(2)}
                      disabled={actionLoading === 'process-rawstory-2' || (stats?.rawstory_pending || 0) === 0}
                    >
                      {actionLoading === 'process-rawstory-2' ? '⏳ Processing...' : '▶️ Process 2 Stories'}
                    </button>
                    <button
                      className="admin-dropdown-item"
                      onClick={() => handleProcessRawstories(15)}
                      disabled={actionLoading === 'process-rawstory-15' || (stats?.rawstory_pending || 0) === 0}
                    >
                      {actionLoading === 'process-rawstory-15' ? '⏳ Processing...' : '▶️ Process 15 Stories'}
                    </button>
                    <button
                      className="admin-dropdown-item"
                      onClick={() => handleProcessRawstories(9999)}
                      disabled={actionLoading === 'process-rawstory-9999' || (stats?.rawstory_pending || 0) === 0}
                    >
                      {actionLoading === 'process-rawstory-9999' ? '⏳ Processing...' : '▶️ Process ALL Stories'}
                    </button>
                  </div>
                )}
              </div>

              <button
                className="admin-refresh-btn"
                onClick={fetchRawstories}
                disabled={rawstoryLoading}
              >
                {rawstoryLoading ? '⏳' : '🔄 Refresh'}
              </button>
            </div>

            <div style={{ marginTop: '15px', fontSize: '13px', color: '#666' }}>
              💡 <strong>Scrape Only</strong> fills the rawstory table. <strong>Process X</strong> picks X stories from rawstory and runs them through the pipeline.
              <br />
              💎 <strong>Paid LLMs</strong> (minimax/minimax-m3, google/gemini-2.5-flash) auto-publish without review.
              Free LLMs go to finalstage for LLM Reviewer + admin review.
            </div>

            {rawstoryLoading ? (
              <div className="admin-loading">Loading raw stories...</div>
            ) : rawstories.length === 0 ? (
              <div className="admin-empty">
                <div className="admin-empty-icon">📭</div>
                <h3>No Pending Raw Stories</h3>
                <p>All raw stories have been processed.</p>
              </div>
            ) : (
              <div className="admin-story-list" style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '15px' }}>
                {rawstories.map(story => (
                  <div key={story.id} className="admin-story-card" style={{ marginBottom: '10px' }}>
                    <div className="admin-story-main">
                      <div className="admin-story-header">
                        <h4 className="admin-story-title">{story.title || 'Untitled'}</h4>
                        <div className="admin-story-badges">
                          <span className={`admin-badge ${story.status}`}>{story.status}</span>
                          {story.source_website && <span className="admin-badge batch">{story.source_website}</span>}
                        </div>
                      </div>
                      <div className="admin-story-meta">
                        <span>📅 {story.scraped_at ? new Date(story.scraped_at).toLocaleDateString() : 'Unknown'}</span>
                        {story.genre && <span>🏷️ {story.genre}</span>}
                        <span>📊 {story.raw_content ? wordCount(story.raw_content) : 0} words</span>
                      </div>
                      <div className="admin-story-preview">
                        <small>{story.raw_content ? story.raw_content.substring(0, 200) + '...' : 'No content'}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* NEW: Final Stage Section */}
      <div className="admin-section" style={{ marginTop: '30px' }}>
        <div className="admin-section-header" onClick={() => setShowFinalstageSection(!showFinalstageSection)} style={{ cursor: 'pointer' }}>
          <h2>🔍 Final Stage Stories ({finalstageStories.length})</h2>
          <span>{showFinalstageSection ? '▲ Hide' : '▼ Show'}</span>
        </div>

        {showFinalstageSection && (
          <>
            <div className="admin-bulk-actions">
              <button
                className="admin-approve-all-btn"
                onClick={handleProcessFinalstage}
                disabled={actionLoading === 'process-finalstage'}
              >
                {actionLoading === 'process-finalstage' ? '⏳ Processing...' : '🔍 Run LLM Review'}
              </button>
              <button
                className="admin-refresh-btn"
                onClick={fetchFinalstageStories}
              >
                🔄 Refresh
              </button>
            </div>

            {finalstageStories.length === 0 ? (
              <div className="admin-empty">
                <div className="admin-empty-icon">📭</div>
                <h3>No Final Stage Stories</h3>
                <p>Stories awaiting LLM review will appear here.</p>
              </div>
            ) : (
              <div className="admin-story-list" style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '15px' }}>
                {finalstageStories.map(story => (
                  <div key={story.id} className="admin-story-card" style={{ marginBottom: '10px' }}>
                    <div className="admin-story-main">
                      <div className="admin-story-header">
                        <h4 className="admin-story-title">{story.paraphrased_title || story.original_title}</h4>
                        <div className="admin-story-badges">
                          {story.genre && <span className="admin-badge genre">{story.genre}</span>}
                          {story.paraphrasing_model && <span className="admin-badge batch">{story.paraphrasing_model}</span>}
                          {story.llm_review_status && (
                            <span className={`admin-badge review-status ${story.llm_review_status}`}>
                              {story.llm_review_status}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="admin-story-meta">
                        <span>📅 {story.created_at ? new Date(story.created_at).toLocaleDateString() : 'Unknown'}</span>
                        <span>📝 {wordCount(story.paraphrased_content || story.original_content)} words</span>
                      </div>
                      <div className="admin-story-preview">
                        <small>{story.paraphrased_content ? story.paraphrased_content.substring(0, 200) + '...' : 'No content'}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* NEW: AI WriteStory Section
          Shows user-submitted stories awaiting the LLM-review and image-gen
          stages of the story_queue pipeline, and exposes one-click buttons
          to trigger each stage instantly (no need to wait for the hourly
          cron jobs in StoryQueueScheduler). */}
      <div className="admin-section" style={{ marginTop: '30px' }}>
        <div
          className="admin-section-header"
          onClick={() => setShowAiWriteStorySection(!showAiWriteStorySection)}
          style={{ cursor: 'pointer' }}
        >
          <h2>
            🤖 AI WriteStory (pipeline: idea reservoir → image → publish)
            {aiWriteStoryStats && aiWriteStoryStats.counts && (
              <span style={{ fontSize: '0.7em', color: '#666', marginLeft: '10px' }}>
                (
                {' • '}awaiting image: {aiWriteStoryStats.counts.accepted || 0}
                {' • '}image ready: {aiWriteStoryStats.counts.ready || 0}
                {' • '}published: {aiWriteStoryStats.counts.published || 0}
                {' • '}rejected: {aiWriteStoryStats.counts.rejected || 0})
              </span>
            )}
          </h2>
          <span>{showAiWriteStorySection ? '▲ Hide' : '▼ Show'}</span>
        </div>

        {showAiWriteStorySection && (
          <>
            {/* Trigger buttons — one per pipeline stage */}
            <div className="admin-bulk-actions">
              
              <button
                className="admin-approve-all-btn"
                onClick={() => handleWriteStoryJob('image', 'ai')}
                disabled={aiWriteStoryJobRunning !== null || (aiWriteStoryStats?.counts?.accepted || 0) === 0}
                title="Generate cover images for all stories with status=3"
              >
                {aiWriteStoryJobRunning === 'image'
                  ? '⏳ Generating images…'
                  : `🎨 Generate Images Now (${aiWriteStoryStats?.counts?.accepted || 0} ready)`}
              </button>
              <button
                className="admin-approve-all-btn"
                onClick={() => handleWriteStoryJob('finalize', 'ai')}
                disabled={aiWriteStoryJobRunning !== null || (aiWriteStoryStats?.counts?.ready || 0) === 0}
                title="Publish all stories with status=4 to the main stories table (and submit opted-in ones to the competition)"
              >
                {aiWriteStoryJobRunning === 'finalize'
                  ? '⏳ Publishing…'
                  : `📚 Publish Now (${aiWriteStoryStats?.counts?.ready || 0} ready)`}
              </button>
              <button
                className="admin-refresh-btn"
                onClick={fetchAiWriteStoryQueue}
                disabled={aiWriteStoryLoading}
              >
                {aiWriteStoryLoading ? '⏳' : '🔄 Refresh'}
              </button>
            </div>

            {aiWriteStoryJobResult && (
              <div
                className={`admin-message ${aiWriteStoryJobResult.ok ? 'success' : 'error'}`}
                style={{ marginTop: '10px' }}
              >
                <span>{aiWriteStoryJobResult.ok ? '✅' : '❌'}</span> {aiWriteStoryJobResult.summary}
                <button className="admin-message-close" onClick={() => setWriteStoryJobResult(null)}>✕</button>
              </div>
            )}

            <div style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
              💡 The WriteStory pipeline runs 3 stages in order: <strong>Review</strong> (smut check + summary) → <strong>Image</strong> (cover art) → <strong>Publish</strong> (moves to <code>stories</code> table + enters competition if opted-in).
              <br />
              The hourly cron driver handles this automatically, but the buttons above let you trigger each stage on demand.
            </div>

            {aiWriteStoryLoading ? (
              <div className="admin-loading">Loading WriteStory queue…</div>
            ) : (
              <>
                {/* AI stories skip review — they go straight from the idea reservoir
                    into story_queue at status=3 (awaiting image), bypassing the
                    LLM-reviewer stage entirely. No "Queued for Review" subsection
                    is needed here. */}

                {/* (b) Stories queued for image generation */}
                <div style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0 }}>🅱️ Queued for Image Generation ({aiWriteStoryImageQueue.length})</h3>
                    <button
                      className="admin-approve-all-btn"
                      onClick={() => handleWriteStoryJob('image', 'ai')}
                      disabled={aiWriteStoryJobRunning !== null}
                      style={{ fontSize: '0.85em', padding: '4px 12px' }}
                      title="Re-process status=3 + status=6 rows now"
                    >
                      🔁 Retry Now
                    </button>
                  </div>
                  {aiWriteStoryImageQueue.length === 0 ? (
                    <div className="admin-empty" style={{ padding: '15px' }}>
                      <p>✅ No stories waiting for cover images. The queue is clear.</p>
                    </div>
                  ) : (
                    <div className="admin-story-list" style={{ maxHeight: '350px', overflowY: 'auto', marginTop: '8px' }}>
                      {aiWriteStoryImageQueue.map(story => (
                        <div key={story.id} className="admin-story-card" style={{ marginBottom: '8px' }}>
                          <div className="admin-story-main">
                            <div className="admin-story-header">
                              <h4 className="admin-story-title">{story.title || 'Untitled'}</h4>
                              <div className="admin-story-badges">
                                <span className="admin-badge" style={{ background: '#cce5ff', color: '#004085' }}>awaiting image</span>
                                {story.genre && <span className="admin-badge genre">{story.genre}</span>}
                                {story.llm_model && <span className="admin-badge batch">🤖 {story.llm_model}</span>}
                                {story.smut_confidence !== null && story.smut_confidence !== undefined && (
                                  <span className="admin-badge" style={{ background: '#d4edda', color: '#155724' }}>
                                    smut {Math.round((story.smut_confidence || 0) * 100)}%
                                  </span>
                                )}
                                {story.submit_to_competition === 1 && <span className="admin-badge batch">🏆 Competition</span>}
                              </div>
                            </div>
                            <div className="admin-story-meta">
                              <span>✍️ {story.author || 'Anonymous'}</span>
                              <span>📅 {story.submitted_at ? new Date(story.submitted_at).toLocaleString() : 'Unknown'}</span>
                              <span>✅ Reviewed {story.llm_reviewed_at ? new Date(story.llm_reviewed_at).toLocaleString() : '—'}</span>
                              <span>🆔 {story.queue_id}</span>
                            </div>
                            {story.summary && (
                              <div className="admin-story-preview">
                                <small><strong>Summary:</strong> {story.summary}</small>
                              </div>
                            )}
                            <div style={{ marginTop: '6px' }}>
                              <button
                                className="admin-reject-btn"
                                onClick={() => handleWriteStorySkipImage(story.queue_id, story.id, 'ai')}
                                disabled={aiWriteStorySkipInProgress === story.id}
                                style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.85em' }}
                                title="Skip image generation for this story and publish it with a placeholder cover"
                              >
                                {aiWriteStorySkipInProgress === story.id
                                  ? '⏳ Skipping…'
                                  : '⏭️ Skip & publish without image'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* (d) Stories in image_retry (status = 6) - waiting for next backoff window */}
                {aiWriteStoryRetryQueue.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h3>🅳 Image Retry Queue ({aiWriteStoryRetryQueue.length})</h3>
                    <p style={{ fontSize: '0.85em', color: '#666', margin: '4px 0 8px 0' }}>
                      These stories failed all image backends and are waiting for their backoff window to elapse before the next attempt (max 5 retries, then auto-rejected).
                    </p>
                    <div className="admin-story-list" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                      {aiWriteStoryRetryQueue.map(story => (
                        <div key={story.id} className="admin-story-card" style={{ marginBottom: '8px', borderLeft: '3px solid #ffc107' }}>
                          <div className="admin-story-main">
                            <div className="admin-story-header">
                              <h4 className="admin-story-title">{story.title || 'Untitled'}</h4>
                              <div className="admin-story-badges">
                                <span className="admin-badge" style={{ background: '#fff3cd', color: '#856404' }}>
                                  🕒 retry #{story.image_retry_count || 0}
                                </span>
                                {story.genre && <span className="admin-badge genre">{story.genre}</span>}
                                {story.submit_to_competition === 1 && <span className="admin-badge batch">🏆 Competition</span>}
                              </div>
                            </div>
                            <div className="admin-story-meta">
                              <span>✍️ {story.author || 'Anonymous'}</span>
                              <span>🆔 {story.queue_id}</span>
                              <span>⏰ Next attempt: {story.image_next_retry_at ? new Date(story.image_next_retry_at).toLocaleString() : 'pending'}</span>
                            </div>
                            {story.image_error && (
                              <div className="admin-story-preview" style={{ background: '#fff8e1', padding: '6px 8px', borderRadius: '4px' }}>
                                <small><strong>Last error:</strong> <code style={{ fontSize: '0.9em' }}>{story.image_error.substring(0, 240)}{story.image_error.length > 240 ? '…' : ''}</code></small>
                              </div>
                            )}
                            <div style={{ marginTop: '6px', display: 'flex', gap: '8px' }}>
                              <button
                                className="admin-approve-all-btn"
                                onClick={() => handleWriteStoryJob('image', 'ai')}
                                disabled={aiWriteStoryJobRunning !== null}
                                style={{ fontSize: '0.85em', padding: '4px 12px' }}
                                title="Trigger image generation now (will pick up rows whose backoff window has elapsed)"
                              >
                                🔁 Retry Now
                              </button>
                              <button
                                className="admin-reject-btn"
                                onClick={() => handleWriteStorySkipImage(story.queue_id, story.id, 'ai')}
                                disabled={aiWriteStorySkipInProgress === story.id}
                                style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.85em' }}
                                title="Stop retrying; publish this story with a placeholder cover now"
                              >
                                {aiWriteStorySkipInProgress === story.id
                                  ? '⏳ Skipping…'
                                  : '⏭️ Skip & publish'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* (c) Ready to publish (image ready) — collapsible info, action is via the Publish Now button above */}
                {aiWriteStoryReadyQueue.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h3>🅲 Ready to Publish ({aiWriteStoryReadyQueue.length})</h3>
                    <div className="admin-story-list" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {aiWriteStoryReadyQueue.map(story => (
                        <div key={story.id} className="admin-story-card" style={{ marginBottom: '8px' }}>
                          <div className="admin-story-main">
                            <div className="admin-story-header">
                              <h4 className="admin-story-title">{story.title || 'Untitled'}</h4>
                              <div className="admin-story-badges">
                                <span className="admin-badge" style={{ background: '#d4edda', color: '#155724' }}>image ready</span>
                                {story.genre && <span className="admin-badge genre">{story.genre}</span>}
                                {story.image_model && <span className="admin-badge batch">🎨 {story.image_model}</span>}
                                {story.submit_to_competition === 1 && <span className="admin-badge batch">🏆 Competition</span>}
                              </div>
                            </div>
                            <div className="admin-story-meta">
                              <span>✍️ {story.author || 'Anonymous'}</span>
                              <span>🖼️ {story.image_generated_at ? new Date(story.image_generated_at).toLocaleString() : '—'}</span>
                              <span>🆔 {story.queue_id}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>


      {/* NEW: User WriteStory Section
          Shows user-submitted stories awaiting the LLM-review and image-gen
          stages of the story_queue pipeline, and exposes one-click buttons
          to trigger each stage instantly (no need to wait for the hourly
          cron jobs in StoryQueueScheduler). */}
      <div className="admin-section" style={{ marginTop: '30px' }}>
        <div
          className="admin-section-header"
          onClick={() => setShowUserWriteStorySection(!showUserWriteStorySection)}
          style={{ cursor: 'pointer' }}
        >
          <h2>
            👤 User WriteStory (user-submitted stories: review → image → publish)
            {userWriteStoryStats && userWriteStoryStats.counts && (
              <span style={{ fontSize: '0.7em', color: '#666', marginLeft: '10px' }}>
                (pending: {userWriteStoryStats.counts.pending || 0}
                {' • '}awaiting image: {userWriteStoryStats.counts.accepted || 0}
                {' • '}image ready: {userWriteStoryStats.counts.ready || 0}
                {' • '}published: {userWriteStoryStats.counts.published || 0}
                {' • '}rejected: {userWriteStoryStats.counts.rejected || 0})
              </span>
            )}
          </h2>
          <span>{showUserWriteStorySection ? '▲ Hide' : '▼ Show'}</span>
        </div>

        {showUserWriteStorySection && (
          <>
            {/* Trigger buttons — one per pipeline stage */}
            <div className="admin-bulk-actions">
              <button
                className="admin-approve-all-btn"
                onClick={() => handleWriteStoryJob('review', 'user')}
                disabled={userWriteStoryJobRunning !== null || (userWriteStoryStats?.counts?.pending || 0) === 0}
                title="Run LLM smut-check + summary on all stories with status=1"
              >
                {userWriteStoryJobRunning === 'review'
                  ? '⏳ Running review…'
                  : `🔍 Run Review Now (${userWriteStoryStats?.counts?.pending || 0} pending)`}
              </button>
              <button
                className="admin-approve-all-btn"
                onClick={() => handleWriteStoryJob('image', 'user')}
                disabled={userWriteStoryJobRunning !== null || (userWriteStoryStats?.counts?.accepted || 0) === 0}
                title="Generate cover images for all stories with status=3"
              >
                {userWriteStoryJobRunning === 'image'
                  ? '⏳ Generating images…'
                  : `🎨 Generate Images Now (${userWriteStoryStats?.counts?.accepted || 0} ready)`}
              </button>
              <button
                className="admin-approve-all-btn"
                onClick={() => handleWriteStoryJob('finalize', 'user')}
                disabled={userWriteStoryJobRunning !== null || (userWriteStoryStats?.counts?.ready || 0) === 0}
                title="Publish all stories with status=4 to the main stories table (and submit opted-in ones to the competition)"
              >
                {userWriteStoryJobRunning === 'finalize'
                  ? '⏳ Publishing…'
                  : `📚 Publish Now (${userWriteStoryStats?.counts?.ready || 0} ready)`}
              </button>
              <button
                className="admin-refresh-btn"
                onClick={fetchUserWriteStoryQueue}
                disabled={userWriteStoryLoading}
              >
                {userWriteStoryLoading ? '⏳' : '🔄 Refresh'}
              </button>
            </div>

            {userWriteStoryJobResult && (
              <div
                className={`admin-message ${userWriteStoryJobResult.ok ? 'success' : 'error'}`}
                style={{ marginTop: '10px' }}
              >
                <span>{userWriteStoryJobResult.ok ? '✅' : '❌'}</span> {userWriteStoryJobResult.summary}
                <button className="admin-message-close" onClick={() => setWriteStoryJobResult(null)}>✕</button>
              </div>
            )}

            <div style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
              💡 The WriteStory pipeline runs 3 stages in order: <strong>Review</strong> (smut check + summary) → <strong>Image</strong> (cover art) → <strong>Publish</strong> (moves to <code>stories</code> table + enters competition if opted-in).
              <br />
              The hourly cron driver handles this automatically, but the buttons above let you trigger each stage on demand.
            </div>

            {userWriteStoryLoading ? (
              <div className="admin-loading">Loading WriteStory queue…</div>
            ) : (
              <>
                {/* (a) Stories queued for review */}
                <div style={{ marginTop: '20px' }}>
                  <h3>🅰️ Queued for Review ({userWriteStoryReviewQueue.length})</h3>
                  {userWriteStoryReviewQueue.length === 0 ? (
                    <div className="admin-empty" style={{ padding: '15px' }}>
                      <p>✅ No stories waiting for LLM review. The queue is clear.</p>
                    </div>
                  ) : (
                    <div className="admin-story-list" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                      {userWriteStoryReviewQueue.map(story => (
                        <div key={story.id} className="admin-story-card" style={{ marginBottom: '8px' }}>
                          <div className="admin-story-main">
                            <div className="admin-story-header">
                              <h4 className="admin-story-title">{story.title || 'Untitled'}</h4>
                              <div className="admin-story-badges">
                                <span className="admin-badge" style={{ background: '#fff3cd', color: '#856404' }}>awaiting review</span>
                                {story.genre && <span className="admin-badge genre">{story.genre}</span>}
                                {story.submit_to_competition === 1 && <span className="admin-badge batch">🏆 Competition</span>}
                              </div>
                            </div>
                            <div className="admin-story-meta">
                              <span>✍️ {story.author || 'Anonymous'}</span>
                              <span>📅 {story.submitted_at ? new Date(story.submitted_at).toLocaleString() : 'Unknown'}</span>
                              <span>🆔 {story.queue_id}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* (b) Stories queued for image generation */}
                <div style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0 }}>🅱️ Queued for Image Generation ({userWriteStoryImageQueue.length})</h3>
                    <button
                      className="admin-approve-all-btn"
                      onClick={() => handleWriteStoryJob('image', 'user')}
                      disabled={userWriteStoryJobRunning !== null}
                      style={{ fontSize: '0.85em', padding: '4px 12px' }}
                      title="Re-process status=3 + status=6 rows now"
                    >
                      🔁 Retry Now
                    </button>
                  </div>
                  {userWriteStoryImageQueue.length === 0 ? (
                    <div className="admin-empty" style={{ padding: '15px' }}>
                      <p>✅ No stories waiting for cover images. The queue is clear.</p>
                    </div>
                  ) : (
                    <div className="admin-story-list" style={{ maxHeight: '350px', overflowY: 'auto', marginTop: '8px' }}>
                      {userWriteStoryImageQueue.map(story => (
                        <div key={story.id} className="admin-story-card" style={{ marginBottom: '8px' }}>
                          <div className="admin-story-main">
                            <div className="admin-story-header">
                              <h4 className="admin-story-title">{story.title || 'Untitled'}</h4>
                              <div className="admin-story-badges">
                                <span className="admin-badge" style={{ background: '#cce5ff', color: '#004085' }}>awaiting image</span>
                                {story.genre && <span className="admin-badge genre">{story.genre}</span>}
                                {story.llm_model && <span className="admin-badge batch">🤖 {story.llm_model}</span>}
                                {story.smut_confidence !== null && story.smut_confidence !== undefined && (
                                  <span className="admin-badge" style={{ background: '#d4edda', color: '#155724' }}>
                                    smut {Math.round((story.smut_confidence || 0) * 100)}%
                                  </span>
                                )}
                                {story.submit_to_competition === 1 && <span className="admin-badge batch">🏆 Competition</span>}
                              </div>
                            </div>
                            <div className="admin-story-meta">
                              <span>✍️ {story.author || 'Anonymous'}</span>
                              <span>📅 {story.submitted_at ? new Date(story.submitted_at).toLocaleString() : 'Unknown'}</span>
                              <span>✅ Reviewed {story.llm_reviewed_at ? new Date(story.llm_reviewed_at).toLocaleString() : '—'}</span>
                              <span>🆔 {story.queue_id}</span>
                            </div>
                            {story.summary && (
                              <div className="admin-story-preview">
                                <small><strong>Summary:</strong> {story.summary}</small>
                              </div>
                            )}
                            <div style={{ marginTop: '6px' }}>
                              <button
                                className="admin-reject-btn"
                                onClick={() => handleWriteStorySkipImage(story.queue_id, story.id, 'user')}
                                disabled={userWriteStorySkipInProgress === story.id}
                                style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.85em' }}
                                title="Skip image generation for this story and publish it with a placeholder cover"
                              >
                                {userWriteStorySkipInProgress === story.id
                                  ? '⏳ Skipping…'
                                  : '⏭️ Skip & publish without image'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* (d) Stories in image_retry (status = 6) - waiting for next backoff window */}
                {userWriteStoryRetryQueue.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h3>🅳 Image Retry Queue ({userWriteStoryRetryQueue.length})</h3>
                    <p style={{ fontSize: '0.85em', color: '#666', margin: '4px 0 8px 0' }}>
                      These stories failed all image backends and are waiting for their backoff window to elapse before the next attempt (max 5 retries, then auto-rejected).
                    </p>
                    <div className="admin-story-list" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                      {userWriteStoryRetryQueue.map(story => (
                        <div key={story.id} className="admin-story-card" style={{ marginBottom: '8px', borderLeft: '3px solid #ffc107' }}>
                          <div className="admin-story-main">
                            <div className="admin-story-header">
                              <h4 className="admin-story-title">{story.title || 'Untitled'}</h4>
                              <div className="admin-story-badges">
                                <span className="admin-badge" style={{ background: '#fff3cd', color: '#856404' }}>
                                  🕒 retry #{story.image_retry_count || 0}
                                </span>
                                {story.genre && <span className="admin-badge genre">{story.genre}</span>}
                                {story.submit_to_competition === 1 && <span className="admin-badge batch">🏆 Competition</span>}
                              </div>
                            </div>
                            <div className="admin-story-meta">
                              <span>✍️ {story.author || 'Anonymous'}</span>
                              <span>🆔 {story.queue_id}</span>
                              <span>⏰ Next attempt: {story.image_next_retry_at ? new Date(story.image_next_retry_at).toLocaleString() : 'pending'}</span>
                            </div>
                            {story.image_error && (
                              <div className="admin-story-preview" style={{ background: '#fff8e1', padding: '6px 8px', borderRadius: '4px' }}>
                                <small><strong>Last error:</strong> <code style={{ fontSize: '0.9em' }}>{story.image_error.substring(0, 240)}{story.image_error.length > 240 ? '…' : ''}</code></small>
                              </div>
                            )}
                            <div style={{ marginTop: '6px', display: 'flex', gap: '8px' }}>
                              <button
                                className="admin-approve-all-btn"
                                onClick={() => handleWriteStoryJob('image', 'user')}
                                disabled={userWriteStoryJobRunning !== null}
                                style={{ fontSize: '0.85em', padding: '4px 12px' }}
                                title="Trigger image generation now (will pick up rows whose backoff window has elapsed)"
                              >
                                🔁 Retry Now
                              </button>
                              <button
                                className="admin-reject-btn"
                                onClick={() => handleWriteStorySkipImage(story.queue_id, story.id, 'user')}
                                disabled={userWriteStorySkipInProgress === story.id}
                                style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.85em' }}
                                title="Stop retrying; publish this story with a placeholder cover now"
                              >
                                {userWriteStorySkipInProgress === story.id
                                  ? '⏳ Skipping…'
                                  : '⏭️ Skip & publish'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* (c) Ready to publish (image ready) — collapsible info, action is via the Publish Now button above */}
                {userWriteStoryReadyQueue.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h3>🅲 Ready to Publish ({userWriteStoryReadyQueue.length})</h3>
                    <div className="admin-story-list" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {userWriteStoryReadyQueue.map(story => (
                        <div key={story.id} className="admin-story-card" style={{ marginBottom: '8px' }}>
                          <div className="admin-story-main">
                            <div className="admin-story-header">
                              <h4 className="admin-story-title">{story.title || 'Untitled'}</h4>
                              <div className="admin-story-badges">
                                <span className="admin-badge" style={{ background: '#d4edda', color: '#155724' }}>image ready</span>
                                {story.genre && <span className="admin-badge genre">{story.genre}</span>}
                                {story.image_model && <span className="admin-badge batch">🎨 {story.image_model}</span>}
                                {story.submit_to_competition === 1 && <span className="admin-badge batch">🏆 Competition</span>}
                              </div>
                            </div>
                            <div className="admin-story-meta">
                              <span>✍️ {story.author || 'Anonymous'}</span>
                              <span>🖼️ {story.image_generated_at ? new Date(story.image_generated_at).toLocaleString() : '—'}</span>
                              <span>🆔 {story.queue_id}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>


      {/* NEW: AI Idea Generation & Reservoir Section
          Configuration + manual trigger for the story_ideas reservoir that
          feeds the StoryIdeaConsumer → story_queue pipeline. The three AI
          sections (this one + the AI WriteStory section below it + the
          Runtime Status panel) form the AI pipeline. */}
      <div className="admin-section" style={{ marginTop: '30px' }}>
        <div
          className="admin-section-header"
          onClick={() => setShowAiGenSection(!showAiGenSection)}
          style={{ cursor: 'pointer' }}
        >
          <h2>
            🪄 AI Idea Generation &amp; Reservoir
            {aiGenStatus && (
              <span style={{ fontSize: '0.7em', color: '#666', marginLeft: '10px' }}>
                (available: {aiGenStatus.availableIdeas ?? 0}
                {' • '}processing: {aiGenStatus.processingIdeas ?? 0}
                {' • '}used: {aiGenStatus.usedIdeas ?? 0}
                {' • '}failed: {aiGenStatus.failedIdeas ?? 0}
                {' • '}total: {aiGenStatus.totalIdeas ?? 0})
              </span>
            )}
          </h2>
          <span>{showAiGenSection ? '▲ Hide' : '▼ Show'}</span>
        </div>

        {showAiGenSection && (
          <>
            {aiGenLoading ? (
              <div className="admin-loading">Loading AI Story Generation status…</div>
            ) : (
              <>
                {/* --- Status row: Available / Last Generation / Last Batch Size / Last Consumer Run --- */}
                <div className="admin-stats" style={{ marginBottom: '20px' }}>
                  <div className="admin-stat-card pending">
                    <div className="admin-stat-icon">📚</div>
                    <div className="admin-stat-info">
                      <span className="admin-stat-number">{aiGenStatus?.availableIdeas ?? 0}</span>
                      <span className="admin-stat-label">Available Ideas</span>
                    </div>
                  </div>
                  <div className="admin-stat-card approved">
                    <div className="admin-stat-icon">🕒</div>
                    <div className="admin-stat-info">
                      <span className="admin-stat-number" style={{ fontSize: '0.95em' }}>
                        {aiGenStatus?.lastRunAt
                          ? new Date(aiGenStatus.lastRunAt).toLocaleString()
                          : '—'}
                      </span>
                      <span className="admin-stat-label">Last Generation Time</span>
                    </div>
                  </div>
                  <div className="admin-stat-card rawstory">
                    <div className="admin-stat-icon">📦</div>
                    <div className="admin-stat-info">
                      <span className="admin-stat-number">{aiGenStatus?.lastBatchGenerated ?? 0}</span>
                      <span className="admin-stat-label">Last Batch Size</span>
                    </div>
                  </div>
                  <div className="admin-stat-card batches">
                    <div className="admin-stat-icon">📈</div>
                    <div className="admin-stat-info">
                      <span className="admin-stat-number" style={{ fontSize: '0.95em' }}>
                        {aiGenStatus?.lastRunStatus || '—'}
                      </span>
                      <span className="admin-stat-label">Last Run Status</span>
                    </div>
                  </div>
                </div>

                {/* --- NEW: Consumer status row --- */}
                <div className="admin-stats" style={{ marginBottom: '20px' }}>
                  <div className="admin-stat-card pending">
                    <div className="admin-stat-icon">🔥</div>
                    <div className="admin-stat-info">
                      <span className="admin-stat-number" style={{ fontSize: '0.95em' }}>
                        {aiGenStatus?.consumerLastRunAt
                          ? new Date(aiGenStatus.consumerLastRunAt).toLocaleString()
                          : '—'}
                      </span>
                      <span className="admin-stat-label">Last Consumer Run Time</span>
                    </div>
                  </div>
                  <div className="admin-stat-card approved">
                    <div className="admin-stat-icon">📤</div>
                    <div className="admin-stat-info">
                      <span className="admin-stat-number">{aiGenStatus?.consumerLastBatchConsumed ?? 0}</span>
                      <span className="admin-stat-label">Last Consumer Batch</span>
                    </div>
                  </div>
                  <div className="admin-stat-card rawstory">
                    <div className="admin-stat-icon">📊</div>
                    <div className="admin-stat-info">
                      <span className="admin-stat-number" style={{ fontSize: '0.95em' }}>
                        {aiGenStatus?.consumerLastRunStatus || '—'}
                      </span>
                      <span className="admin-stat-label">Last Consumer Status</span>
                    </div>
                  </div>
                </div>

                {(aiGenStatus?.lastRunMessage || aiGenStatus?.consumerLastRunMessage) && (
                  <div style={{
                    marginBottom: '15px',
                    padding: '10px 12px',
                    background: '#f4f6f8',
                    border: '1px solid #e1e4e8',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#444'
                  }}>
                    {aiGenStatus.lastRunMessage && (
                      <div style={{ marginBottom: aiGenStatus.consumerLastRunMessage ? '6px' : 0 }}>
                        <strong>🪄 Last generation run:</strong> {aiGenStatus.lastRunMessage}
                      </div>
                    )}
                    {aiGenStatus.consumerLastRunMessage && (
                      <div>
                        <strong>🔥 Last consumer run:</strong> {aiGenStatus.consumerLastRunMessage}
                      </div>
                    )}
                  </div>
                )}

                {/* --- NEW: LLM provider stats panel (Direct MiniMax M3 primary + OpenRouter fallback) --- */}
                <h3 style={{ marginTop: '10px' }}>🔌 LLM Provider (Direct MiniMax M3 → OpenRouter fallback)</h3>
                {llmProviderStats ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '12px',
                    marginBottom: '15px'
                  }}>
                    <ProviderStatsCard title="Story Idea Generator" stats={llmProviderStats.storyIdeaGenerator} />
                    <ProviderStatsCard title="Paraphraser" stats={llmProviderStats.paraphraser} />
                  </div>
                ) : (
                  <div className="admin-loading" style={{ marginBottom: '15px' }}>
                    {llmProviderStatsLoading ? 'Loading provider stats…' : 'No provider stats yet (start a generation or consumer run).'}
                  </div>
                )}

                {/* --- NEW: Per-genre idea reservoir table --- */}
                <h3 style={{ marginTop: '20px' }}>📋 Idea Reservoir by Genre</h3>
                <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                  Idea generation for a genre only auto-runs when its reservoir drops below the threshold ({aiGenStatus?.threshold ?? 5}).
                  Use the per-genre <strong>Generate</strong> button to force-run idea generation, and the <strong>Consume</strong> button
                  to drain up to 10 AVAILABLE ideas into the story pipeline. The per-genre lock prevents double-runs.
                  Each replenish internally calls MiniMax up to 5 times with batches of 20 ideas (proven to fit within the 60s timeout),
                  accumulating to the 100-idea reservoir target. Watch <code>pm2 logs | grep MINIMAX</code> for the
                  5 REQUEST/RESPONSE pairs per replenish per genre.
                </p>
                <GenreReservoirTable
                  reservoir={genreReservoir}
                  threshold={aiGenStatus?.threshold ?? 5}
                  inFlight={genreReplenishInFlight}
                  consumeInFlight={genreConsumeInFlight}
                  onTrigger={handleReplenishGenre}
                  onConsume={handleConsumeGenre}
                />

                {/* --- Configuration form --- */}
                <h3 style={{ marginTop: '10px' }}>⚙️ Configuration</h3>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '14px',
                    marginTop: '10px',
                    marginBottom: '15px',
                    padding: '16px',
                    border: '1px solid #e1e4e8',
                    borderRadius: '8px',
                    background: '#fafbfc'
                  }}
                >
                  {/* Enabled toggle */}
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Enabled</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={aiGenEnabled}
                        onChange={(e) => setAiGenEnabled(e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '13px', color: '#555' }}>
                        {aiGenEnabled ? 'ON — generation active' : 'OFF — generation paused'}
                      </span>
                    </div>
                  </label>

                  {/* Ideas per Batch */}
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Ideas per Batch</span>
                    <input
                      type="number"
                      min="1"
                      max="500"
                      value={aiGenIdeasPerBatch}
                      onChange={(e) => setAiGenIdeasPerBatch(e.target.value)}
                      style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px' }}
                    />
                    <span style={{ fontSize: '11px', color: '#888' }}>1–500 (default 100)</span>
                  </label>

                  {/* Replenishment Threshold */}
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Replenishment Threshold</span>
                    <input
                      type="number"
                      min="0"
                      max="10000"
                      value={aiGenThreshold}
                      onChange={(e) => setAiGenThreshold(e.target.value)}
                      style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px' }}
                    />
                    <span style={{ fontSize: '11px', color: '#888' }}>Generate when available ≤ this</span>
                  </label>

                  {/* Schedule 1 */}
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Schedule 1</span>
                    <input
                      type="time"
                      value={aiGenSchedule1}
                      onChange={(e) => setAiGenSchedule1(e.target.value)}
                      style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px' }}
                    />
                    <span style={{ fontSize: '11px', color: '#888' }}>Daily cron time (local)</span>
                  </label>

                  {/* Schedule 2 */}
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Schedule 2</span>
                    <input
                      type="time"
                      value={aiGenSchedule2}
                      onChange={(e) => setAiGenSchedule2(e.target.value)}
                      style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px' }}
                    />
                    <span style={{ fontSize: '11px', color: '#888' }}>Second daily cron time (local)</span>
                  </label>
                </div>

                {/* --- NEW: Consumer (drain → story_queue) config form --- */}
                <h3 style={{ marginTop: '20px' }}>🔥 Consumer (Story Generation) Config</h3>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '14px',
                    marginTop: '10px',
                    marginBottom: '15px',
                    padding: '16px',
                    border: '1px solid #fde2e2',
                    borderRadius: '8px',
                    background: '#fff8f8'
                  }}
                >
                  {/* Consumer Enabled toggle */}
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Consumer Enabled</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={aiGenConsumerEnabled}
                        onChange={(e) => setAiGenConsumerEnabled(e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '13px', color: '#555' }}>
                        {aiGenConsumerEnabled ? 'ON — draining reservoir' : 'OFF — reservoir idle'}
                      </span>
                    </div>
                  </label>

                  {/* Consumer Schedule (cron) */}
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Consumer Schedule (cron)</span>
                    <input
                      type="text"
                      value={aiGenConsumerSchedule}
                      onChange={(e) => setAiGenConsumerSchedule(e.target.value)}
                      placeholder="*/5 * * * *"
                      style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', fontFamily: 'monospace' }}
                    />
                    <span style={{ fontSize: '11px', color: '#888' }}>e.g. <code>*/5 * * * *</code> = every 5 min</span>
                  </label>

                  {/* Consumer Batch Size */}
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Consumer Batch Size</span>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={aiGenConsumerBatchSize}
                      onChange={(e) => setAiGenConsumerBatchSize(e.target.value)}
                      style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px' }}
                    />
                    <span style={{ fontSize: '11px', color: '#888' }}>Ideas consumed per tick (1–20)</span>
                  </label>

                  {/* Consumer Min Available */}
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Min Available</span>
                    <input
                      type="number"
                      min="0"
                      max="10000"
                      value={aiGenConsumerMinAvailable}
                      onChange={(e) => setAiGenConsumerMinAvailable(e.target.value)}
                      style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px' }}
                    />
                    <span style={{ fontSize: '11px', color: '#888' }}>Only consume when available ≥ N</span>
                  </label>
                </div>

                {/* --- Action buttons --- */}
                <div className="admin-bulk-actions">
                  <button
                    className="admin-refresh-btn"
                    onClick={handleSaveAiGenConfig}
                    disabled={aiGenSaving}
                    style={{ backgroundColor: '#28a745', color: 'white' }}
                  >
                    {aiGenSaving ? '⏳ Saving…' : '💾 Save Configuration'}
                  </button>
                  <button
                    className="admin-approve-all-btn"
                    onClick={handleGenerateIdeasNow}
                    disabled={aiGenRunning || !aiGenConfig?.enabled}
                    title={!aiGenConfig?.enabled
                      ? 'Enable generation in the config above first'
                      : 'Calls the same scheduler.replenish() the cron uses. May take 1-3 minutes.'}
                  >
                    {aiGenRunning
                      ? '⏳ Generating… (may take 1-3 min)'
                      : `✨ Generate Ideas Now (${aiGenConfig?.ideasPerBatch || 100})`}
                  </button>
                  <button
                    className="admin-approve-all-btn"
                    onClick={handleConsumeIdeasNow}
                    disabled={aiGenConsuming || !aiGenConfig?.consumerEnabled || (aiGenStatus?.availableIdeas ?? 0) === 0}
                    style={{ backgroundColor: '#dc3545', color: 'white' }}
                    title={!aiGenConfig?.consumerEnabled
                      ? 'Enable consumer in the config above first'
                      : (aiGenStatus?.availableIdeas ?? 0) === 0
                        ? 'No AVAILABLE ideas to consume'
                        : `Calls the same scheduler.consumeNow() the cron uses. Up to ${aiGenConfig?.consumerBatchSize || 1} idea(s), each sent to MiniMax for 1000-2500 word generation.`}
                  >
                    {aiGenConsuming
                      ? '⏳ Consuming… (1-2 min per idea)'
                      : `🔥 Consume Ideas Now (${Math.min(aiGenStatus?.availableIdeas ?? 0, aiGenConfig?.consumerBatchSize || 1)})`}
                  </button>
                  <button
                    className="admin-refresh-btn"
                    onClick={fetchAiGenData}
                    disabled={aiGenLoading}
                  >
                    {aiGenLoading ? '⏳' : '🔄 Refresh Status'}
                  </button>
                </div>

                {aiGenResult && (
                  <div
                    className={`admin-message ${aiGenResult.ok ? 'success' : 'error'}`}
                    style={{ marginTop: '12px' }}
                  >
                    <span>{aiGenResult.ok ? '✅' : '❌'}</span> {aiGenResult.summary}
                    <button className="admin-message-close" onClick={() => setAiGenResult(null)}>✕</button>
                  </div>
                )}

                <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
                  💡 <strong>Generate Ideas Now</strong> calls the same <code>scheduler.replenish()</code> the daily cron uses — the LLM batch is requested, validated, deduplicated, and saved to the <code>story_ideas</code> reservoir.
                  <br />
                  🔥 <strong>Consume Ideas Now</strong> calls the same <code>scheduler.consumeNow()</code> the per-5-min consumer cron uses — it picks up AVAILABLE ideas, sends each to <strong>minimax/minimax-m3</strong> via the existing <code>Paraphraser</code> for a 1000–2500 word story, and inserts the result into <code>story_queue</code> with <code>status=1, author='AI StoryMeal'</code>. The EXISTING <code>StoryQueueReviewer → ImageGenerator → Finalizer</code> chain picks it up from there — no new review/image/publish workflows were created.
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Runtime Status Section */}
      <div className="admin-section" style={{ marginTop: '30px' }}>
        <div
          className="admin-section-header"
          onClick={() => setShowRuntimeStatusSection(!showRuntimeStatusSection)}
          style={{ cursor: 'pointer' }}
        >
          <h2>🩺 Runtime Status (Recent Failures)</h2>
          <span>{showRuntimeStatusSection ? '▲ Hide' : '▼ Show'}</span>
        </div>

        {showRuntimeStatusSection && (
          <>
            <RuntimeStatusPanel
              llmFailures={llmFailures}
              errorOutputs={errorOutputs}
              loading={llmFailuresLoading}
            />
            <div style={{ fontSize: '12px', color: '#666' }}>
              💡 In-memory ring buffer; max 10 of each kind per service. Resets on server restart.
              Useful for diagnosing what just broke without grepping logs. Each entry has service, provider, stage,
              message, and (when available) context tags like <code>ideaId</code> / <code>queueId</code> / genre.
            </div>
          </>
        )}
      </div>

      {/* Prestage/Stage Management Section */}
      {stats && (
      <div className="admin-section" style={{ marginTop: '30px' }}>
        <div className="admin-section-header" onClick={() => setShowPrestageSection(!showPrestageSection)} style={{ cursor: 'pointer' }}>
          <h2>📋 Prestage & Stage Management</h2>
          <span>{showPrestageSection ? '▲ Hide' : '▼ Show'}</span>
        </div>

          {showPrestageSection && (
            <>
              <div className="admin-stats" style={{ marginBottom: '20px' }}>
                {/* FIX: Show actual loaded data counts, not global stats */}
                <div className="admin-stat-card prestage">
                  <div className="admin-stat-icon">📥</div>
                  <div className="admin-stat-info">
                    <span className="admin-stat-number">{prestageStories.length}</span>
                    <span className="admin-stat-label">Prestage Loaded ({stats?.prestage_pending || 0} pending in DB)</span>
                  </div>
                </div>
                <div className="admin-stat-card processed">
                  <div className="admin-stat-icon">✅</div>
                  <div className="admin-stat-info">
                    <span className="admin-stat-number">{stageStories.length}</span>
                    <span className="admin-stat-label">Stage Loaded ({stats?.stage_processed || 0} total in DB)</span>
                  </div>
                </div>
                <div className="admin-stat-card batches">
                  <div className="admin-stat-icon">📦</div>
                  <div className="admin-stat-info">
                    <span className="admin-stat-number">{stats?.total_batches || 0}</span>
                    <span className="admin-stat-label">Total Batches</span>
                  </div>
                </div>
              </div>

              <div className="admin-bulk-actions">
                <button
                  className="admin-approve-all-btn"
                  onClick={handleProcessPrestage}
                  disabled={actionLoading === 'process-prestage' || stats.prestage_pending === 0}
                >
                  {actionLoading === 'process-prestage' ? '⏳ Processing...' : '▶️ Process Prestage (Validate)'}
                </button>
                <button
                  className="admin-approve-all-btn"
                  onClick={handleProcessStage}
                  disabled={actionLoading === 'process-stage' || stats.stage_processed === 0}
                >
                  {actionLoading === 'process-stage' ? '⏳ Processing...' : '✍️ Process Stage (Paraphrase)'}
                </button>
                {/* COMMENTED OUT: LLM Review FinalStage is redundant when paraphrasing is done by LLM
                <button
                  className="admin-approve-all-btn"
                  onClick={handleProcessFinalstage}
                  disabled={actionLoading === 'process-finalstage'}
                >
                  {actionLoading === 'process-finalstage' ? '⏳ Processing...' : '🔍 LLM Review FinalStage'}
                </button>
                */}
                <button
                  className="admin-reject-all-btn"
                  onClick={handleDeleteAllPrestage}
                  disabled={actionLoading === 'delete-prestage' || stats.prestage_pending === 0}
                >
                  {actionLoading === 'delete-prestage' ? '⏳' : '🗑️ Delete All Prestage'}
                </button>
              </div>

              {/* Prestage Stories List */}
              {prestageStories.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <h3>📥 Prestage Stories ({prestageStories.length})</h3>
                  <div className="admin-story-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {prestageStories.map(story => (
                      <div key={story.id} className="admin-story-card" style={{ marginBottom: '10px' }}>
                        <div className="admin-story-main">
                          <div className="admin-story-header">
                            <h4 className="admin-story-title">{story.title || 'Untitled'}</h4>
                            <div className="admin-story-badges">
                              <span className={`admin-badge ${story.status}`}>{story.status}</span>
                              {story.source_website && <span className="admin-badge batch">{story.source_website}</span>}
                            </div>
                          </div>
                          <div className="admin-story-meta">
                            <span>📅 {story.scraped_at ? new Date(story.scraped_at).toLocaleDateString() : 'Unknown'}</span>
                            {story.suggested_genre && <span>🏷️ {story.suggested_genre}</span>}
                          </div>
                          <div className="admin-story-preview">
                            <small>{story.plain_content ? story.plain_content.substring(0, 200) + '...' : 'No content'}</small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stage Stories List */}
              {stageStories.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <h3>✅ Stage Stories ({stageStories.length})</h3>
                  <div className="admin-story-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {stageStories.map(story => (
                      <div key={story.id} className="admin-story-card" style={{ marginBottom: '10px' }}>
                        <div className="admin-story-main">
                          <div className="admin-story-header">
                            <h4 className="admin-story-title">{story.title}</h4>
                            <div className="admin-story-badges">
                              <span className="admin-badge genre">{story.genre}</span>
                              {story.batchname && <span className="admin-badge batch">{story.batchname}</span>}
                            </div>
                          </div>
                          <div className="admin-story-meta">
                            <span>📅 {story.processed_at ? new Date(story.processed_at).toLocaleDateString() : 'Unknown'}</span>
                            <span>📊 Confidence: {(story.confidence_score * 100).toFixed(0)}%</span>
                            {story.validation_model && <span>🤖 {story.validation_model}</span>}
                          </div>
                          <div className="admin-story-preview">
                            <small>{story.content ? story.content.substring(0, 200) + '...' : 'No content'}</small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="admin-confirm-overlay">
          <div className="admin-confirm-modal">
            <h3>⚠️ Confirm {confirmAction === 'approve_all' ? 'Approve All' : 'Reject All'}</h3>
            <p>
              {confirmAction === 'approve_all'
                ? `Are you sure you want to approve ALL ${pendingStories.length} pending stories? They will be published immediately.`
                : `Are you sure you want to reject and delete ALL ${pendingStories.length} pending stories? This cannot be undone.`
              }
            </p>
            <div className="admin-confirm-actions">
              <button
                className="admin-confirm-yes"
                onClick={confirmAction === 'approve_all' ? handleApproveAll : handleRejectAll}
              >
                Yes, {confirmAction === 'approve_all' ? 'Approve All' : 'Reject All'}
              </button>
              <button
                className="admin-confirm-no"
                onClick={() => setConfirmAction(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Published Stories Section */}
      <div className="admin-section" style={{ marginTop: '30px' }}>
        <div className="admin-section-header" onClick={() => setShowPublishedSection(!showPublishedSection)} style={{ cursor: 'pointer' }}>
          <h2>📚 Manage Published Stories</h2>
          <span>{showPublishedSection ? '▲ Hide' : '▼ Show'}</span>
        </div>

        {showPublishedSection && (
          <>
            <div className="admin-filters" style={{ marginBottom: '15px' }}>
              <input
                type="text"
                placeholder="Search by title, author, or ID..."
                value={publishedSearch}
                onChange={(e) => setPublishedSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handlePublishedSearch(); }}
                className="admin-search-input"
                style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
              />
              <button
                className="admin-refresh-btn"
                onClick={handlePublishedSearch}
                disabled={publishedLoading}
              >
                {publishedLoading ? '⏳' : '🔍 Search'}
              </button>
              <button
                className="admin-refresh-btn"
                onClick={() => { setPublishedSearch(''); fetchPublishedStories('', 0); }}
                style={{ marginLeft: '5px' }}
              >
                🔄 Reset
              </button>
            </div>

            <div className="admin-bulk-actions" style={{ marginBottom: '10px' }}>
              <span className="admin-bulk-count">{publishedTotal} total published stories</span>
            </div>

            {publishedLoading ? (
              <div className="admin-loading">Loading published stories...</div>
            ) : publishedStories.length === 0 ? (
              <div className="admin-empty">
                <div className="admin-empty-icon">📭</div>
                <h3>No Stories Found</h3>
                <p>{publishedSearch ? 'No stories match your search.' : 'No published stories yet.'}</p>
              </div>
            ) : (
              <div className="admin-story-list">
                {publishedStories.map(story => (
                  <div key={story.story_id} className="admin-story-card">
                    <div className="admin-story-main">
                      <div className="admin-story-header">
                        <h4 className="admin-story-title">{story.title || 'Untitled'}</h4>
                        <div className="admin-story-badges">
                          {story.genre && <span className="admin-badge genre">{story.genre}</span>}
                          {story.is_user_submitted === 1 && <span className="admin-badge batch">User Submitted</span>}
                        </div>
                      </div>
                      <div className="admin-story-meta">
                        <span>✍️ {story.author || 'Anonymous'}</span>
                        <span>📅 {story.created_at ? new Date(story.created_at).toLocaleDateString() : 'Unknown'}</span>
                        <span>❤️ {story.like_count || 0} likes</span>
                        <span>👁️ {story.view_count || 0} views</span>
                        <span>💬 {story.comment_count || 0} comments</span>
                        <span>🆔 {story.story_id}</span>
                      </div>
                    </div>
                    <div className="admin-story-actions">
                      <button
                        className="admin-reject-btn"
                        onClick={() => handleDeletePublishedStory(story.story_id, story.title)}
                        disabled={deletingStoryId === story.story_id}
                        style={{ backgroundColor: '#dc3545', color: 'white' }}
                      >
                        {deletingStoryId === story.story_id ? '⏳ Deleting...' : '🗑️ Delete'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {publishedTotal > 20 && (
              <div className="admin-pagination" style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '15px' }}>
                <button
                  className="admin-refresh-btn"
                  onClick={() => fetchPublishedStories(publishedSearch, publishedOffset - 20)}
                  disabled={publishedOffset === 0}
                >
                  ← Previous
                </button>
                <span style={{ padding: '8px 12px' }}>
                  Page {Math.floor(publishedOffset / 20) + 1} of {Math.ceil(publishedTotal / 20)}
                </span>
                <button
                  className="admin-refresh-btn"
                  onClick={() => fetchPublishedStories(publishedSearch, publishedOffset + 20)}
                  disabled={publishedOffset + 20 >= publishedTotal}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pending Stories */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>📋 Stories Needing Review</h2>
          <span className="admin-count">{pendingStories.length} pending</span>
        </div>

        {loading ? (
          <div className="admin-loading">Loading pending stories...</div>
        ) : pendingStories.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty-icon">🎉</div>
            <h3>All Caught Up!</h3>
            <p>No stories pending approval.</p>
          </div>
        ) : (
          <div className="admin-story-list">
            {pendingStories.map(story => (
              <div key={story.id} className="admin-story-card">
                <div className="admin-story-main">
                  <div className="admin-story-header">
                    <h3 className="admin-story-title">
                      {story.paraphrased_title || story.original_title}
                    </h3>
                    <div className="admin-story-badges">
                      {story.genre && <span className="admin-badge genre">{story.genre}</span>}
                      {story.batchname && <span className="admin-badge batch">{story.batchname}</span>}
                      {story.llm_review_status && (
                        <span className={`admin-badge review-status ${story.llm_review_status}`}>
                          {story.llm_review_status === 'needs_admin' ? '⏳ Needs Review' : story.llm_review_status}
                        </span>
                      )}
                      {story.confidence_score && (
                        <span className={`admin-badge confidence ${story.confidence_score >= 0.7 ? 'high' : story.confidence_score >= 0.4 ? 'medium' : 'low'}`}>
                          {(story.confidence_score * 100).toFixed(0)}% confidence
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="admin-story-meta">
                    <span>📅 {story.created_at ? new Date(story.created_at).toLocaleDateString() : 'Unknown date'}</span>
                    <span>📝 {wordCount(story.paraphrased_content || story.original_content)} words</span>
                    {story.validation_model && <span>🤖 {story.validation_model}</span>}
                    {story.llm_review_feedback && (
                      <span className="admin-llm-feedback">💬 LLM: {story.llm_review_feedback}</span>
                    )}
                  </div>

                  <div className="admin-story-preview" onClick={() => toggleExpand(story.id)}>
                    <div className={expandedStory === story.id ? '' : 'collapsed'}>
                      {expandedStory === story.id
                        ? (story.paraphrased_content || story.original_content || 'No content')
                            .split('\n')
                            .map((paragraph, i) => (
                              <p key={i} className="admin-story-paragraph">{paragraph}</p>
                            ))
                        : truncate(stripHtml(story.paraphrased_content || story.original_content), 300)
                      }
                    </div>
                    {(story.paraphrased_content || story.original_content || '').length > 300 && (
                      <button className="admin-expand-btn">
                        {expandedStory === story.id ? '▲ Show less' : '▼ Show full story'}
                      </button>
                    )}
                  </div>
                </div>

                <div className="admin-story-actions">
                  <button
                    className="admin-approve-btn"
                    onClick={() => handleApprove(story.id)}
                    disabled={actionLoading === story.id || actionLoading === 'bulk'}
                  >
                    {actionLoading === story.id ? '⏳ Processing...' : '✅ Approve'}
                  </button>
                  <button
                    className="admin-reject-btn"
                    onClick={() => handleReject(story.id)}
                    disabled={actionLoading === story.id || actionLoading === 'bulk'}
                  >
                    {actionLoading === story.id ? '⏳' : '🗑️ Reject'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;